<!-- Copyright 2023, Common Good Learning Tools LLC -->
<template><div style="width:100vw; height:calc(100vh - 60px); padding:0 12px 12px 12px;" v-bind="outer_css">
	<div v-if="!resources_loaded" class="text-center" style="padding-top:120px; font-size:24px;"><b>LOADING RESOURCES...</b></div>
	<div v-if="resources_loaded">
		<div class="d-flex align-center pl-2 pt-3 pb-3">
			<h3 class="k-custom-heading-color d-flex align-center">
				<k-btn
					icon
					small
					class="mr-2"
					color="#000"
					accessible-label="Show resources table"
					@click="show_table"
				><k-icon style="font-size:32px">fas {{is_case_resource_set ? 'fa-cubes' : 'fa-cubes-stacked'}}</k-icon></k-btn>
				<div style="margin:-2px 0 0 4px;" v-html="resource_set.resource_set_title"></div>
				<k-btn icon small style="margin:-3px 0 0 10px" accessible-label="Open resource set help" @click="U.show_help('framework_index')" color="light-blue"><k-icon>fas fa-info-circle</k-icon></k-btn>
			</h3>
			<k-spacer/>

			<div v-if="show_tree_view_toggle">
				<k-btn small color="#000" outlined class="mt-1 ml-2 mr-2 k-tight-btn" @click="go_to_tree_view"><k-icon small class="mr-2">fas fa-tree</k-icon>Framework View</k-btn>
			</div>

			<div v-if="!viewing_resource" class="mr-1" style="max-width:320px;"><k-text-field v-model="search_text" dense outlined rounded clearable prepend-inner-icon="fa fa-search" prepend-decorative accessible-label="Search resources" label="Search resources" background-color="#fff" single-line hide-details @input="handle_search"></k-text-field></div>

			<k-menu accessible-popover accessible-label="Resource set actions" content-id="resource-set-actions-panel" :transition="false" bottom left><template v-slot:activator="{on, attrs}"><k-btn v-on="on" v-bind="attrs" color="#333" icon class="ml-3 mr-1" v-show="user_can_create_new&&!viewing_resource" aria-label="Open resource set actions"><k-icon>fas fa-ellipsis-vertical</k-icon></k-btn></template>
				<k-list id="resource-set-actions-panel" dense tabindex="-1" aria-label="Resource set actions">
					<k-list-item v-if="!is_case_resource_set" @click="rename_set"><k-list-item-icon><k-icon small>fas fa-font</k-icon></k-list-item-icon><k-list-item-title>Rename resource set</k-list-item-title></k-list-item>
					<k-list-item v-if="!is_case_resource_set" @click="add_set_editor"><k-list-item-icon><k-icon small>fas fa-user</k-icon></k-list-item-icon><k-list-item-title>Add an editor for the resource set</k-list-item-title></k-list-item>
					<k-list-item @click="create_new_resource"><k-list-item-icon><k-icon small>fas fa-circle-plus</k-icon></k-list-item-icon><k-list-item-title>Add new resource</k-list-item-title></k-list-item>
					<k-list-item @click="start_resource_import"><k-list-item-icon><k-icon small>fas fa-file-import</k-icon></k-list-item-icon><k-list-item-title>Import resources, metadata, and/or alignments</k-list-item-title></k-list-item>
					<k-list-item v-if="tba_framework_identifier" @click="start_resource_export"><k-list-item-icon><k-icon small>fas fa-file-export</k-icon></k-list-item-icon><k-list-item-title>Export alignments to CSV</k-list-item-title></k-list-item>
					<k-list-item v-if="tba_framework_identifier && site_config.linked_cureum_instance" @click="resource_synch_to_cureum"><k-list-item-icon><k-icon small>fas fa-briefcase-arrow-right</k-icon></k-list-item-icon><k-list-item-title>Synch alignments to Cureum</k-list-item-title></k-list-item>
					<k-list-item v-if="tba_framework_identifier && user_can_align" @click="batch_unalign_all"><k-list-item-icon><k-icon small>fas fa-xmarks-lines</k-icon></k-list-item-icon><k-list-item-title>Clear all alignments to this framework</k-list-item-title></k-list-item>
					<k-list-item v-if="user_can_admin" @click="show_manage_framework_user_rights=true"><k-list-item-icon><k-icon small class="red--text text--darken-3">fas fa-users</k-icon></k-list-item-icon><k-list-item-title class="red--text text--darken-3">Manage user rights for framework…</k-list-item-title></k-list-item>
					<k-list-item v-if="show_tree_view_toggle" @click="go_to_tree_view" style="border-top:1px solid #999;" class="pt-2"><k-list-item-icon><k-icon small>fas fa-tree</k-icon></k-list-item-icon><k-list-item-title>Manage resource set metadata<br>and archives from the <b>TREE VIEW</b></k-list-item-title></k-list-item>
				</k-list>
			</k-menu>

		</div>

		<div class="k-framework-list-wrapper k-resource-set-wrapper elevation-3" :class="wrapper_css" :style="wrapper_style"><div class="k-framework-list-wrapper-inner">

			<!-- START OF FRAMEWORK WE'RE CURRENTLY ALIGNING TOO, PLUS SWITCHER -->
			<div v-if="!creating_resource" class="d-flex align-center px-2 pb-3 mt-3" style="border-bottom:1px solid #666;">

				<k-btn v-show="viewing_resource" class="mr-2 elevation-0" small fab color="primary" dark accessible-label="Show resource table" @click="show_table"><k-icon>fas fa-table</k-icon></k-btn>

				<k-spacer v-if="!tba_framework_identifier || viewing_resource"/>

				<div>
					<FrameworkSwitcher2 color="primary" 
						btn_size="large"
						:current_selected_framework_identifier="tba_framework_identifier" 
						:large_btn_content="framework_switcher_btn_content"
						:framework_record_list="framework_records"
						@framework_selected="set_tba_framework" 
					/>
				</div>

				<div v-if="tba_framework_identifier" class="mx-3" style="font-size:18px; line-height:22px;" v-html="tba_framework_record_descriptor"></div>

				<k-btn v-if="tba_framework_identifier && !viewing_resource" icon small color="primary" accessible-label="Clear selected framework" @click="clear_tba_framework"><k-icon style="font-size:28px">fas fa-circle-xmark</k-icon></k-btn>

				<k-spacer v-if="!tba_framework_identifier || viewing_resource"/>

				<k-btn v-show="viewing_resource && tba_framework_identifier" class="ml-2 elevation-0" small fab color="black" dark accessible-label="Open alignment settings" @click="settings_showing=true"><k-icon>fas fa-gear</k-icon></k-btn>

			</div>
			<!-- END OF FRAMEWORK WE'RE ALIGNING TO -->

			<!-- START OF BATCH SETTINGS and ALIGNMENT SETTINGS -->
			<div v-if="tba_framework_identifier" class="d-flex align-start k-resource-set-alignment-settings" style="font-size:16px; line-height:20px;" :style="settings_wrapper_style">
				<k-spacer v-if="!viewing_resource"/>

				<!-- START OF LEFT SIDE, ONLY SHOWN WHEN VIEWING SET TABLE -->
				<div v-if="!viewing_resource" class="ml-2" style="font-size:18px">
					<div v-if="!tfr_compute_paused && is_case_resource_set" class="mb-4 text-center">
						<div v-if="resource_set_branches_to_align.length==0">Aligning all resources</div>
						<div v-else>Aligning resource set “branches”:
							<k-chip v-for="(identifier) in resource_set_branches_to_align" :key="identifier" small close class="my-1 ml-1" @click:close="remove_branch_for_alignment(identifier)"><div v-html="U.generate_cfassociation_node_uri_title(resource_set.framework_record.cfo.cfitems[identifier], 20)"></div></k-chip>
						</div>
						<div class="mt-2">
							<k-btn v-if="!selecting_resource_set_branches_to_align" small outlined class="k-tight-btn k-nocaps-btn" color="primary" @click="select_branches_for_alignment">Select resource set “branches” for alignment</k-btn>
							<k-btn v-else small class="k-tight-btn k-nocaps-btn" color="primary" @click="stop_selecting_branches_for_alignment">Done selecting branches</k-btn>
						</div>
					</div>

					<div v-if="!tfr_compute_paused && !batch_align_settings_showing" class="text-center mt-4">
						<k-btn class="ml-8" color="pink darken-3" dark @click="batch_align_start_clicked"><k-icon small class="mr-2">fas fa-wand-magic-sparkles</k-icon>Batch Align…</k-btn>
					</div>

					<!-- ---------- START OF BATCH ALIGN CONTROLS ---------- -->
					<div v-show="batch_align_settings_showing" class="pink lighten-5 mt-2 px-3 pt-3 pb-3 mb-3 mx-auto" style="border-radius:10px; font-size:15px; width:560px;">

						<div v-if="suggestions_to_compute<=0 && !tfr_compute_paused" class="d-flex">
							<k-btn color="pink darken-3" :style="all_suggestions_clean?'opacity:0.5':''" dark class="k-tight-btn" @click="get_all_resource_alignment_suggestions"><k-icon small class="mr-2">fas fa-robot</k-icon>
								<span v-if="suggestions_to_compute<0">Compute All Suggestions</span>
								<span v-else-if="all_suggestions_clean">All Suggestions Computed<k-icon small class="ml-2">fas fa-check</k-icon></span>
								<span v-else>Re-Compute All Suggestions</span>
							
							<!-- {{(suggestions_to_compute==0)?'Re-Compute All Suggestions':'Compute All Suggestions'}} -->
							</k-btn>
							<k-spacer/>
							<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-on="on" v-show="batch_align_available && n_aligned_items_to_tba_framework > 0" icon color="indigo accent-4" class="ml-3" @click="validate_alignments"><k-icon large>fas fa-magnifying-glass-chart</k-icon></k-btn></template>Compile alignment validation stats</k-tooltip>
							<k-btn icon color="#222" dark class="ml-4" accessible-label="Close batch align settings" @click="batch_align_settings_showing=false"><k-icon large>fas fa-circle-xmark</k-icon></k-btn>
						</div>

						<div v-if="suggestions_to_compute>0" class="d-flex align-center">
							<b style="font-size:18px" class="mr-4">Computing Suggestions…</b>
							<div class="d-flex">
								<k-spacer/>
								<div style="width:180px; position:relative; margin-top:2px">
									<k-progress-linear v-model="suggestions_progress" color="green darken-1" height="24"></k-progress-linear>
									<div style="position:absolute; right:4px; top:3px; font-size:12px; font-weight:bold">{{table_filtered_resources.length - suggestions_to_compute}} / {{this.table_filtered_resources.length}}</div>
								</div>
								<k-btn small color="red darken-3" class="ml-2 k-tight-btn" dark @click="cancel_get_all_suggestions"><k-icon small class="mr-1">fas fa-xmark</k-icon>Stop</k-btn>
								<k-spacer/>
							</div>
						</div>

						<div v-if="!tfr_compute_paused && batch_align_available" class="mt-4 d-flex" style="font-size:16px">
							<k-spacer/>
							<div class="white py-1 px-2 elevation-1" style="border-radius:6px">Mean top suggestion score: <b>{{mean_sm_score}}</b></div>
							<k-spacer/>
						</div>

						<div v-show="batch_align_available && !tfr_compute_paused" class="mt-4">
							<div class="d-flex align-center">
								<k-checkbox class="shrink mt-0 pt-0 d-inline-block" hide-details v-model="batch_min_alignments_is_set"></k-checkbox>
								<div class="text-right" style="flex:0 1 auto">Min auto-alignments per resource:</div>
								<b class="grey darken-4 white--text ml-2 mr-1" style="font-size:16px; width:40px; text-align:center; border-radius:5px;">{{(batch_min_alignments==-1)?'–':batch_min_alignments}}</b>
								<div style="width:220px; height:32px"><k-slider v-if="batch_min_alignments_is_set" v-model="batch_min_alignments" hide-details dense min="1" max="10" step="1" thumb-label></k-slider></div>
							</div>
							<div class="d-flex align-center">
								<k-checkbox class="shrink mt-0 pt-0 d-inline-block" hide-details v-model="batch_max_alignments_is_set"></k-checkbox>
								<div class="text-right" style="flex:0 1 auto">Max auto-alignments per resource:</div>
								<b class="grey darken-4 white--text ml-2 mr-1" style="font-size:16px; width:40px; text-align:center; border-radius:5px;">{{(batch_max_alignments==-1)?'–':batch_max_alignments}}</b>
								<div style="width:220px; height:32px"><k-slider v-if="batch_max_alignments_is_set" v-model="batch_max_alignments" hide-details dense min="1" max="10" step="1" thumb-label></k-slider></div>
							</div>
							<div class="d-flex align-center">
								<div class="text-right" style="flex:1 0 auto">Auto-align to standards with suggestion scores ≥</div>
								<b class="grey darken-4 white--text ml-2 mr-1" style="font-size:16px; width:40px; text-align:center; border-radius:5px;">{{batch_simscore_threshold}}</b>
								<div style="width:220px"><k-slider v-model="batch_simscore_threshold" hide-details dense min="0" max="1000" step="10" thumb-label></k-slider></div>
							</div>
							<!-- note that we need the v-show for batch_alignments_to_create to calculate n_batch_alignments_to_create -->
							<div class="d-flex align-center mt-2" v-show="batch_alignments_to_create.length>0">
								<div style="font-size:16px; flex:1 1 auto" class="mr-3 text-right"><nobr>These batch settings will result in <b class="grey darken-4 white--text mx-1" style="display:inline-block; padding:0 6px; text-align:center; border-radius:5px;">{{n_batch_alignments_to_create}}</b> auto-{{U.ps('alignment',n_batch_alignments_to_create)}}</nobr></div>
								<k-btn v-if="n_batch_alignments_to_create>0" color="pink darken-3" dark @click="do_batch_alignments">  <k-icon small class="mr-2">fas fa-wand-magic-sparkles</k-icon>Do It!  </k-btn>
							</div>
						</div>

					</div>
				<!-- ---------- END OF BATCH ALIGN CONTROLS ---------- -->

				</div>
				<!-- END OF LEFT SIDE -->

				<k-spacer v-if="!viewing_resource"/>

				<!-- ---------------- START OF SETTINGS: shown (always when viewing resource list) or (if settings_showing when viewing a resource) ---------------- -->
				<div v-if="!tfr_compute_paused && (!viewing_resource || settings_showing)" :style="settings_style" :class="viewing_resource?'elevation-5':''">

					<div v-if="viewing_resource" style="position:absolute; right:4px; top:4px;">
						<k-btn icon color="#000" dark accessible-label="Close alignment settings" @click="close_settings_panel"><k-icon style="font-size:28px;">fas fa-circle-xmark</k-icon></k-btn>
					</div>

					<div class="mt-1 text-center" style="font-size:20px;"><k-icon color="black" class="mr-2" style="margin-top:-4px;">fas fa-gear</k-icon><b>Alignment Settings</b></div>

					<div class="d-flex mt-3 align-center flex-wrap">
						<div>
							<b class="mr-2">Limit to “branches”:</b>
							<k-btn v-if="!selecting_branches" x-small color="primary" outlined class="k-tight-btn ml-1 mr-2" @click="select_branches">{{limiters.include_branches.length?'Edit':'Add'}}</k-btn>
							<k-btn v-if="selecting_branches" x-small color="primary" class="k-tight-btn ml-1 mr-2" @click="stop_selecting_branches">Done</k-btn>
						</div>
						<k-chip v-for="(identifier) in limiters.include_branches" :key="identifier" small close class="my-1 ml-1" @click:close="remove_branch(identifier)"><div v-if="tba_framework_record.cfo&&tba_framework_record.cfo.cfitems[identifier]" v-html="U.generate_cfassociation_node_uri_title(tba_framework_record.cfo.cfitems[identifier], 20)"></div></k-chip>
					</div>

					<div v-if="tba_available_item_types" class="mt-3 d-flex align-center">
						<b class="mr-2">
							<nobr>Align to item types:</nobr>
							<div class="text-center"><k-btn x-small text color="primary" class="k-tight-btn" style="margin-top:2px" :disabled="item_types_set_to_default" @click="set_default_item_type_limiters">Use Defaults</k-btn></div>
						</b>
						<k-select v-model="limiters.item_types" :items="tba_available_item_types" background-color="#fff" @change="limiters_updated" label="" dense outlined hide-details multiple small-chips deletable-chips>
							<template v-slot:item="{item}"><div style="width:600px">
								<div class="d-flex align-center">
									<k-icon class="mr-2">{{ limiters.item_types.includes(item) ? 'fas fa-square-check' : 'far fa-square' }}</k-icon>
									<div style="font-size:16px;"">{{ item }}</div>
									<div style="font-size:14px;margin-left:4px">({{tba_item_type_counts[item]}})</div>
									<k-btn x-small text color="secondary" class="ml-2" @click.stop="get_item_type_example(item)">Sample<k-icon v-if="item_type_examples[item]" small class="ml-2">fas fa-recycle</k-icon></k-btn>
								</div>
								<div v-show="item_type_examples[item]" class="mt-1 mb-2" style="font-size:14px; line-height:18px;" v-html="item_type_examples[item]"></div>
							</div></template>
						</k-select>
					</div>

					<div class="d-flex align-center mt-4">
						<div class="text-right"><b><nobr>Resource metadata</nobr></b><br>to use:</div>
						<div class="d-flex flex-wrap" style="max-width:500px">
							<k-checkbox class="shrink mt-0 pt-0 ml-4" hide-details v-model="comp_factors.title" @change="comp_factors_changed" label="Title"></k-checkbox>
							<k-checkbox class="shrink mt-0 pt-0 ml-4" hide-details v-model="comp_factors.description" @change="comp_factors_changed" label="Description"></k-checkbox>
							<k-checkbox class="shrink mt-0 pt-0 ml-4" hide-details v-model="comp_factors.full_content" @change="comp_factors_changed" label="Full Content"></k-checkbox>
							<k-checkbox class="shrink mt-0 pt-0 ml-4" hide-details v-model="comp_factors.text" @change="comp_factors_changed" label="Keywords"></k-checkbox>
							<k-checkbox class="shrink mt-0 pt-0 ml-4" hide-details v-model="comp_factors.education_level" @change="comp_factors_changed" label="Ed. Level"></k-checkbox>
							<k-checkbox class="shrink mt-0 pt-0 ml-4" hide-details v-model="comp_factors.search_terms" @change="comp_factors_changed" label="Search Terms"></k-checkbox>
						</div>
					</div>

					<div class="d-flex align-center mt-4">
						<div class="mr-4 text-right"><b>Cross-alignments</b><br>to use:</div>
						<k-checkbox class="shrink mt-0 pt-0 mr-4" hide-details v-model="comp_factors.ai_assocs" @change="comp_factors_changed" label="Auto Crosswalks"></k-checkbox>
						<k-checkbox class="shrink mt-0 pt-0 mr-4" hide-details v-model="comp_factors.sme_assocs" @change="comp_factors_changed" label="SME Crosswalks (if available)"></k-checkbox>
					</div>

					<div class="mt-4 d-flex align-center">
						<div class="mr-2 text-right"><b>Base framework</b><br>for cross-alignments:</div>
						<k-autocomplete :menu-props="'dense'" dense background-color="#fff" hide-details outlined label="" :items="base_framework_options" v-model="base_framework_identifier"></k-autocomplete>
					</div>

					<div class="d-flex mt-4">
						<k-spacer/>
						<k-checkbox class="shrink mt-0 pt-0 mr-4" hide-details v-model="comp_factors.use_descendents" @change="comp_factors_changed" label="Factor descendent matches into match scores for parents"></k-checkbox>
						<k-spacer/>
					</div>

					<div class="d-flex align-center mt-4">
						<b class="mr-3"><nobr>Evidence weighting:</nobr></b>
						<div class="text-right mr-2">Resource<br>metadata</div>
						<b style="flex:0 0 30px; width:30px" class="d-block text-center">{{ (comp_factor_weights.item == 1000) ? '100' : (comp_factor_weights.item / 10).toFixed(1) }}%</b>
						<div style="width:200px" class="ml-2 mr-1">
							<k-slider v-model="item_assoc_weight_pct" hide-details dense min="0" max="6" step="1" ticks="always" tick-size="5"></k-slider>
						</div>
						<b style="flex:0 0 30px; width:30px" class="d-block text-center">{{ (comp_factor_weights.assocs == 1000) ? '100' : (comp_factor_weights.assocs / 10).toFixed(1) }}%</b>
						<div class="text-left ml-3">Cross-<br>alignments</div>
					</div>

				</div>
				<!-- ---------------- END OF SETTINGS ---------------- -->

				<k-spacer v-if="!viewing_resource"/>
			</div>
			<!-- END OF BATCH SETTINGS and ALIGNMENT SETTINGS -->

			<!-- START OF VALIDATION STATS -->
			<div v-if="validation_data&&show_validation_data&&!tfr_compute_paused" v-show="!viewing_resource" class="k-resource-alignment-validation-data">
				<div class="d-flex">
					<div class="mr-3 mt-3 text-center" style="font-size:18px; line-height:24px;">
						<b>Alignment Validation Statistics</b>
						<div class="text-center"><k-btn x-small outlined class="k-tight-btn k-nocaps-btn" color="#000" @click="copy_validation_headers">Copy Headers</k-btn></div>
						<div class="text-center"><k-btn x-small outlined class="k-tight-btn k-nocaps-btn" color="#000" @click="copy_validation_data">Copy Data</k-btn></div>
					</div>

					<table class="k-resource-alignment-validation-table">
						<thead><tr>
							<th># of Resources</th>
							<th style="cursor:pointer;" :class="(validation_chart_plot_type=='mean_highest_rank')?'k-resource-alignment-validation-active-col':''" @click="validation_chart_plot_type='mean_highest_rank'">
								High Sug. in Top 3 (%)<br>
							</th>
							<th style="cursor:pointer;" :class="(validation_chart_plot_type=='mean_highest_rank')?'k-resource-alignment-validation-active-col':''" @click="validation_chart_plot_type='mean_highest_rank'">
								Mean High Rank<br>
								<k-icon x-small>fas fa-chart-line</k-icon>
							</th>
							<th style="cursor:pointer;" :class="(validation_chart_plot_type=='mean_highest_rank_score')?'k-resource-alignment-validation-active-col':''" @click="validation_chart_plot_type='mean_highest_rank_score'">
								Mean High Rank Score<br>
								<k-icon x-small>fas fa-chart-line</k-icon>
							</th>
							<th style="cursor:pointer;" :class="(validation_chart_plot_type=='mean_rank_score')?'k-resource-alignment-validation-active-col':''" @click="validation_chart_plot_type='mean_rank_score'">
								Mean Rank Score<br>
								<k-icon x-small>fas fa-chart-line</k-icon>
							</th>
							<th style="cursor:pointer;" :class="(validation_chart_plot_type=='mean_highest_comp_score')?'k-resource-alignment-validation-active-col':''" @click="validation_chart_plot_type='mean_highest_comp_score'">
								High SM Score<br>
								<k-icon x-small>fas fa-chart-line</k-icon>
							</th>
							<th style="cursor:pointer;" :class="(validation_chart_plot_type=='mean_comp_score')?'k-resource-alignment-validation-active-col':''" @click="validation_chart_plot_type='mean_comp_score'">
								Mean SM Score<br>
								<k-icon x-small>fas fa-chart-line</k-icon>
							</th>
							<th>Mean # Candidates</th>
						</tr></thead>
						<tbody><tr>
							<td>{{validation_data.resources.length}}</td>
							<td>{{validation_data.mean_highest_suggestion_top_3}}</td>
							<td :style="(validation_chart_plot_type=='mean_highest_rank')?'font-weight:bold':''" :class="(validation_chart_plot_type=='mean_highest_rank')?'indigo--text text--darken-3':''">{{validation_data.mean_highest_rank}}</td>
							<td :style="(validation_chart_plot_type=='mean_highest_rank_score')?'font-weight:bold':''" :class="(validation_chart_plot_type=='mean_highest_rank_score')?'indigo--text text--darken-3':''">{{validation_data.mean_highest_rank_score}}</td>
							<td :style="(validation_chart_plot_type=='mean_rank_score')?'font-weight:bold':''" :class="(validation_chart_plot_type=='mean_rank_score')?'indigo--text text--darken-3':''">{{validation_data.mean_rank_score}}</td>
							<td :style="(validation_chart_plot_type=='mean_highest_comp_score')?'font-weight:bold':''" :class="(validation_chart_plot_type=='mean_highest_comp_score')?'indigo--text text--darken-3':''">{{validation_data.mean_highest_comp_score}}</td>
							<td :style="(validation_chart_plot_type=='mean_comp_score')?'font-weight:bold':''" :class="(validation_chart_plot_type=='mean_comp_score')?'indigo--text text--darken-3':''">{{validation_data.mean_comp_score}}</td>
							<td>{{validation_data.mean_n_candidates}}</td>
						</tr></tbody>
					</table>

					<k-btn icon color="#222" dark class="ml-4" accessible-label="Close alignment validation statistics" @click="show_validation_data=false"><k-icon large>fas fa-circle-xmark</k-icon></k-btn>
				</div>

				<div v-if="validation_chart_options" class="k-resource-alignment-validation-chart-outer">
					<div class="text-center mt-1 mb-2" style="font-size:20px;"><b v-html="validation_chart_title"></b></div>
					<highcharts ref="validation_chart" class="chart" :options="validation_chart_options"></highcharts>
				</div>

				<div class="text-center mt-2">
					<k-btn-toggle dense active-class="k-toggle-btn-active-class" class="k-toggle-btn" v-model="validation_graph_order" mandatory>
						<k-btn x-small light value="resource" @click.stop="validate_alignments">Resource Order</k-btn>
						<k-btn x-small light value="descending" @click.stop="validate_alignments">Descending Order</k-btn>
					</k-btn-toggle>
				</div>
			</div>
			<!-- START OF VALIDATION STATS -->

			<v-data-table v-if="!tfr_compute_paused" v-show="!viewing_resource" dense class="k-framework-list-table k-resource-set-table k-sticky-table-header my-2 mt-4" ref="resources_table" xhide-default-footer
				:headers="table_headers"
				:items="table_filtered_resources"
				:options="table_options"
				:footer-props="footer_options"
				:must-sort="true"
				:sort-by.sync="table_sort_by"
				:sort-desc.sync="table_sort_desc"
				:page.sync="current_page"
				:item-class="item=>(item.resource==this.last_viewed_resource?'k-resource-set-last-viewed-resource':'')"
				@pagination="pagination_changed"
			>
				<template v-slot:header.n_tba_alignments="{ item }"><span style="display:inline-block; margin-top:-4px">TBA<br>Alignments ({{total_current_tba_alignments}})</span></template>
				<template v-slot:header.n_cross_alignments="{ item }"><span style="display:inline-block; margin-top:-4px">Cross-<br>Alignments ({{total_cross_alignments}})</span></template>
				<template v-slot:header.alignments_last_updated_at="{ item }"><span style="display:inline-block; margin-top:-4px">Alignments<br>Updated At</span></template>
			
				<template v-if="show_resource_identifiers" v-slot:item.external_resource_id="{ item }"><nobr style="font-size:12px">{{item.external_resource_id}}</nobr></template>
				<template v-slot:item.n_alignments="{ item }"><k-btn fab x-small style="width:28px; height:28px; margin:2px 0" :outlined="!item.n_alignments" class="elevation-0 k-tight-btn" :color="item.icon_color" dark @click="go_to_resource(item.resource_id,$event)"><span :style="(item.n_alignments>999) ? 'font-size:10px' : ( (item.n_alignments > 99) ? 'font-size:12px' : 'font-size:14px')">{{item.n_alignments?item.n_alignments:'–'}}</span></k-btn></template>
				<template v-slot:item.n_tba_alignments="{ item }"><div class="d-flex align-center">
					<k-spacer/>
					<k-btn fab x-small style="width:28px; height:28px; margin:2px 0; letter-spacing:-0.5px" :outlined="!item.n_tba_alignments" class="elevation-0" :color="item.icon_color" dark @click="go_to_resource(item.resource_id,$event)">
						<k-icon v-if="item.no_match" small>fas fa-ban</k-icon>
						<span v-else style="font-size:16px;margin-left:-2px;">{{item.n_tba_alignments?item.n_tba_alignments:'–'}}</span>
					</k-btn>
					<span v-if="batch_align_settings_showing" v-visible="batch_alignments_hash[item.resource_id]" class="d-flex">
						<k-icon class="ml-2" color="pink darken-4" small>fas fa-wand-magic-sparkles</k-icon>
						<div style="width:30px" class="pink--text text--darken-4 text-left pl-1">{{batch_alignments_hash[item.resource_id] ? batch_alignments_hash[item.resource_id].alignments.length : 0}}</div>
						<k-spacer/>
					</span>
					<k-spacer/>
				
				</div></template>
				<template v-slot:item.n_cross_alignments="{ item }"><span :class="item.cross_alignment_cell_class"><b>{{item.n_cross_alignments?item.n_cross_alignments:'–'}}</b></span></template>

				<!-- <template v-slot:item.top_candidate_val="{ item }"><div class="k-resource-factor-tooltip-outer" v-html="item.top_candidate?item.top_candidate.factor_string:''"></div></template> -->
				<template v-slot:item.top_candidate_val="{ item }"><k-tooltip :disabled="!item.top_candidate" bottom><template v-slot:activator="{on}"><div v-on="on" class="d-flex"><k-spacer/>
					<div class="mr-2">{{(item.top_candidate_val == -1) ? '–' : item.top_candidate_val}}</div>
					<div style="width:100px"><k-progress-linear v-model="item.top_candidate_pct" style="border-radius:2px;" :color="candidate_color(item.resource,item.top_candidate)" height="16"></k-progress-linear></div>
					<div v-visible="item.resource.candidate_params_string&&resource_suggestion_dirty(item.resource)" style="font-size:18px; margin-left:2px;">*</div>
				</div></template><div class="k-resource-factor-tooltip-outer" v-html="item.top_candidate?item.top_candidate.factor_string:''"></div></k-tooltip></template>

				<template v-slot:item.resource_title_index="{ item }">
					<k-hover v-slot:default="{hover}"><div class="d-flex align-center">
						<k-btn icon small :color="item.icon_color" dark class="mr-1 my-1" @click="go_to_resource(item.resource_id,$event)"><k-icon :style="item.icon_style" :class="hover?'fa-spin':''">fas fa-{{item.icon}}</k-icon></k-btn>
						<div class="ml-2">
							<a v-html="item.resource_title" @click="go_to_resource(item.resource_id,$event)"></a>
						</div>
					</div></k-hover>
				</template>
				<template v-slot:item.created_at="{ item }"><nobr style="font-size:12px">{{formatted_date(item.created_at)}}</nobr></template>
				<template v-slot:item.alignments_last_updated_at="{ item }"><nobr style="font-size:12px">{{formatted_date(item.alignments_last_updated_at)}}</nobr></template>
			</v-data-table>

			<div v-if="!viewing_resource&&!tfr_compute_paused"" class="d-flex pb-4">
				<k-spacer/>
				<k-checkbox class="shrink mt-0 pt-0" hide-details v-model="show_aligned_resources" :label="`Show Aligned (${n_aligned_items_to_tba_framework})`"></k-checkbox>
				<k-checkbox class="shrink mt-0 pt-0 ml-4" hide-details v-model="show_unaligned_resources" :label="`Show Unaligned (${resources_for_alignment.length - n_aligned_items_to_tba_framework})`"></k-checkbox>
				<k-checkbox class="shrink mt-0 pt-0 ml-4 mr-10" hide-details v-model="show_no_match_resources" :label="`Show Not-Aligned (${n_no_match_to_tba_framework})`"></k-checkbox>
				<k-checkbox class="shrink mt-0 pt-0" hide-details v-model="show_resource_identifiers" label="Show resource IDs"></k-checkbox>
				<k-spacer/>
			</div>
		
			<ResourceView v-if="viewing_resource" ref="resource_viewer" :set_component="this" :resource_set="resource_set" :table_filtered_resources="table_filtered_resources" :current_resource="current_resource" :batch_alignments_to_create="batch_alignments_to_create" :satchel_embed_width="satchel_embed_width" @show_table="show_table" @show_settings="settings_showing=true" @go_to_resource="go_to_resource" @new_resource_created="new_resource_created" @resource_removed="resource_removed" />
		</div></div>

		<FrameworkAdminUsers v-if="show_manage_framework_user_rights"
			:lsdoc_identifier="resource_set_lsdoc_identifier"
			@dialog_cancel="show_manage_framework_user_rights=false"
		/>
	</div></div></template>

<script>
import { mapState, mapGetters } from 'vuex'
import ResourceView from './ResourceView'
import FrameworkSwitcher2 from '../frameworks/FrameworkSwitcher2'
import ResourceImportExportMixin from './ResourceImportExportMixin'
import ResourceAlignmentMixin from './ResourceAlignmentMixin'
import ResourceAlignmentViewSet from './ResourceAlignmentViewSet'
import FrameworkAdminUsers from '../admin/FrameworkAdminUsers'
import CopyBtn from '../utilities/CopyBtn'

export default {
	components: { ResourceView, FrameworkSwitcher2, FrameworkAdminUsers, CopyBtn },
	mixins: [ResourceImportExportMixin, ResourceAlignmentMixin, ResourceAlignmentViewSet],
	props: {
		resource_set_id: { required: true },
		resource_id: { required: true },	// this will be 0 if we're viewing the table
		query: { type: Object, required: false, default() { return {} }},
		// nreq: { type: String, required: false, default() { return ''} },
	},
	data() { return {
		footer_options: {
			itemsPerPageOptions: [20,50,100],
		},
		table_options: {
			itemsPerPage: (this.$store.state.lst.resource_set_list_items_per_page == -1 ? 100 : this.$store.state.lst.resource_set_list_items_per_page),
			page: 1,
		},
		current_page: 1,

		// for the resource viewer
		main_interface_width: 1000,
		satchel_embed_width: 600,

		search_text: '',
		search_re: null,
		cross_alignment_framework_records: [],
		n_aligned_items_to_tba_framework: 0,
		n_no_match_to_tba_framework: 0,
		total_current_tba_alignments: 0,
		total_cross_alignments: 0,
		n_batch_alignments_to_create: 0,
		batch_alignments_hash: {},
		suggestions_to_compute: -1,

		settings_showing: false,
		selecting_branches: false,

		// we use these three data items to pause computations of the "table_filtered_resources" and "batch_alignments_to_create" arrays when we're doing batch processing (see below),
		// because otherwise those computeds take up a lot of processing time for large resource sets
		table_filtered_resources: [],
		batch_alignments_to_create: [],
		all_suggestions_clean: false,	// set as a side effect of compute_batch_alignments_to_create
		tfr_compute_paused: false,
		cached_tfr_array: [],
		cached_batc: [],
		cached_baa: null,
		cached_nra: null,

		last_viewed_resource: null,

		item_type_examples: {},

		show_manage_framework_user_rights: false,

		validation_data: null,
		show_validation_data: false,
		validation_chart_title: '',

		selecting_resource_set_branches_to_align: false,
	}},
	computed: {
		...mapState(['framework_records', 'user_info', 'site_config', 'resource_sets', 'recommended_max_cfitems']),
		...mapGetters(['signed_in']),
		resource_set() {
			let rs = this.resource_sets.find(x=>x.resource_set_id == this.resource_set_id) 
			// if we haven't already loaded this set and it's a CASE set, try to load it now
			if (empty(rs) && isNaN(this.resource_set_id*1)) {
				let framework_record = this.framework_records.find(x=>x.lsdoc_identifier == this.resource_set_id)
				if (U.is_resource_framework(framework_record)) {
					rs = new Resource_Set(framework_record)
					this.$store.state.resource_sets.push(rs)
				}
			}
			return rs
		},
		is_case_resource_set() { return this.resource_set?.framework_record ? true : false },
		resource_set_lsdoc_identifier() {
			if (!this.is_case_resource_set) return ''
			return this.resource_set.framework_record.lsdoc_identifier
		},
		resources_loaded() { return this.resource_set?.resources_loaded },
		resources() { return this.resource_set?.resources },
		resources_for_alignment() {
			// if we're not using case, return all resources
			if (!this.is_case_resource_set) return this.resources
			if (empty(this.resource_set.framework_record?.cfo?.cftree) || this.resources.length == 0) return []

			// recurse nodes to find resources for alignment; this also sets resource_title_index so that resources are displayed in the order they appear in the tree
			let resource_title_index = 0
			let fill_arr = (parent) => {
				for (let child of parent.children) {
					if (U.is_resource_item(child.cfitem)) {
						let resource = this.resources.find(x=>x.resource_id == child.cfitem.identifier)
						if (empty(resource)) {
							continue
						}
						resource.resource_title_index = resource_title_index
						++resource_title_index
						arr.push(resource)
					}
					if (child.children.length > 0) fill_arr(child)
				}
			}

			let arr = []
			if (this.resource_set_branches_to_align.length > 0) {
				// while we do this, check to make sure branches are still valid
				let new_branch_arr = []
				for (let branch_identifier of this.resource_set_branches_to_align) {
					let node = this.resource_set.framework_record.cfo.cfitems[branch_identifier]?.tree_nodes[0]
					if (node) {
						fill_arr(node)
						new_branch_arr.push(branch_identifier)
					}
				}
				this.resource_set_branches_to_align = new_branch_arr
			}
			if (arr.length == 0) {
				fill_arr(this.resource_set.framework_record.cfo.cftree)
			}

			return arr
		},
		viewing_resource() { return this.resource_id != 0 },
		creating_resource() { 
			return this.resource_id == 1
		},
		current_resource() { 
			if (!this.resources_for_alignment || this.resource_id == 0) return null
			return this.resources_for_alignment.find(x=>x.resource_id == this.resource_id) 
		},
		user_can_view_this_resource_set() { 
			if (this.is_case_resource_set) {
				// for case sets, user must currently have edit rights to the framework to be able to view this interface
				return vapp.is_granted('edit_framework', this.resource_set_lsdoc_identifier)
				
			} else {
				// for now we'll assume that for non-case sets, if you're here you can view; we'll get rid of non-case sets eventually...
				return true
			}
		},
		user_can_create_new() { 
			if (this.is_case_resource_set) {
				// for case sets, user must have edit rights to the framework to be able to create new resources
				return vapp.is_granted('edit_framework', this.resource_set_lsdoc_identifier)
				
			} else {
				// for now we'll assume that for non-case sets, if you're here you can create new resources; we'll get rid of non-case sets eventually...
				return true
			}
		},
		user_can_remove() {
			// this only applies when viewing a resource
			if (empty(this.current_resource)) return false

			if (this.is_case_resource_set) {
				// for case sets, user must have edit rights to the framework to be able to remove
				return vapp.is_granted('edit_framework', this.resource_set_lsdoc_identifier)
				
			} else {
				// user must be the creator of either the set or the resource to be able to remove
				if (this.user_info.user_id == this.resource_set.creator_user_id) return true
				if (this.user_info.user_id == this.current_resource.creator_user_id) return true
				return false
			}
		},
		user_can_align() {
			if (this.is_case_resource_set) {
				// for case sets, user must CURRENTLY have edit rights to the framework to be able to make alignments (this should change eventually)
				return vapp.is_granted('edit_framework', this.resource_set_lsdoc_identifier)
				
			} else {
				// for now we'll assume that for non-case sets, if you're here you can make alignments; we'll get rid of non-case sets eventually...
				return true
			}
		},
		user_can_admin() {
			if (!this.signed_in) return false
			if (!this.is_case_resource_set) return false
			return vapp.is_granted('admin_framework', this.resource_set_lsdoc_identifier)
		},
		suggestions_progress() {
			if (this.table_filtered_resources.length == 0 || this.suggestions_to_compute == -1) return 100
			return (this.table_filtered_resources.length - this.suggestions_to_compute) / this.table_filtered_resources.length * 100
		},
		table_sort_by: {
			get() { return this.$store.state.lst.resource_set_table_sort_by },
			set(val) { 
				this.$store.commit('lst_set', ['resource_set_table_sort_by', val]) 
				// we have to keep the table_filtered_resources array in the same order that the table is in
				setTimeout(x=>this.sort_table_filtered_resources())
			}
		},
		table_sort_desc: {
			get() { return this.$store.state.lst.resource_set_table_sort_desc },
			set(val) { 
				this.$store.commit('lst_set', ['resource_set_table_sort_desc', val]) 
				setTimeout(x=>this.sort_table_filtered_resources())
			}
		},
		show_aligned_resources: {
			get() { return this.$store.state.lst.resource_set_show_aligned_resources },
			set(val) { this.$store.commit('lst_set', ['resource_set_show_aligned_resources', val]) }
		},
		show_unaligned_resources: {
			get() { return this.$store.state.lst.resource_set_show_unaligned_resources },
			set(val) { this.$store.commit('lst_set', ['resource_set_show_unaligned_resources', val]) }
		},
		show_no_match_resources: {
			get() { return this.$store.state.lst.resource_set_show_no_match_resources },
			set(val) { this.$store.commit('lst_set', ['resource_set_show_no_match_resources', val]) }
		},
		show_resource_identifiers: {
			get() { return this.$store.state.lst.resource_set_show_resource_identifiers },
			set(val) { this.$store.commit('lst_set', ['resource_set_show_resource_identifiers', val]) }
		},
		resource_set_branches_to_align: {
			get() { return this.$store.state.lst.resource_set_branches_to_align_hash[this.resource_set_id] || [] },
			set(val) { this.$store.commit('lst_set_hash', ['resource_set_branches_to_align_hash', this.resource_set_id, val]) }
		},
		batch_align_settings_showing: {
			get() { return this.$store.state.lst.resource_set_batch_align_settings_showing_hash[this.resource_set_id] },
			set(val) { this.$store.commit('lst_set_hash', ['resource_set_batch_align_settings_showing_hash', this.resource_set_id, val]) }
		},
		batch_min_alignments_is_set: {
			get() { 
				return (this.$store.state.lst.resource_set_batch_min_alignments_hash[this.resource_set_id] != -1) 
			},
			set(val) { 
				if (!val) this.$store.commit('lst_set_hash', ['resource_set_batch_min_alignments_hash', this.resource_set_id, -1])
				else this.$store.commit('lst_set_hash', ['resource_set_batch_min_alignments_hash', this.resource_set_id, 1])
			}
		},
		batch_min_alignments: {
			get() { return this.$store.state.lst.resource_set_batch_min_alignments_hash[this.resource_set_id] ?? -1 },
			set(val) { this.$store.commit('lst_set_hash', ['resource_set_batch_min_alignments_hash', this.resource_set_id, val]) }
		},
		batch_max_alignments_is_set: {
			get() { 
				return (this.$store.state.lst.resource_set_batch_max_alignments_hash[this.resource_set_id] != -1) 
			},
			set(val) { 
				if (!val) this.$store.commit('lst_set_hash', ['resource_set_batch_max_alignments_hash', this.resource_set_id, -1])
				else this.$store.commit('lst_set_hash', ['resource_set_batch_max_alignments_hash', this.resource_set_id, 1])
			}
		},
		batch_max_alignments: {
			get() { return this.$store.state.lst.resource_set_batch_max_alignments_hash[this.resource_set_id] ?? -1 },
			set(val) { this.$store.commit('lst_set_hash', ['resource_set_batch_max_alignments_hash', this.resource_set_id, val]) }
		},
		batch_simscore_threshold: {
			get() { return this.$store.state.lst.resource_set_batch_simscore_threshold_hash[this.resource_set_id] ?? 850 },
			set(val) { this.$store.commit('lst_set_hash', ['resource_set_batch_simscore_threshold_hash', this.resource_set_id, val]) }
		},
		wrapper_css() {
			let s = ''
			if (this.viewing_resource) {
				s += ' k-resource-set-viewing-resource'
				if (vapp.$refs.satchel.show_satchel) s += ' k-resource-set-viewing-resource-embedded-showing'
			}
			return s
		},
		wrapper_style() {
			if (!this.viewing_resource) return ''
			return `width:${this.main_interface_width}px;`
		},
		table_headers() {
			let arr = [{ text: 'Resource', value: 'resource_title_index', sortable: true, align: 'left' }]
			// let arr = [{ text: 'Resource Title', value: 'resource_title', sortable: true, align: 'left' }]
			if (this.tba_framework_identifier) {
				arr.push({ text: 'Top Suggestion', value: 'top_candidate_val', sortable: true, class: 'k-resource-set-table-min', align: 'center' })
				arr.push({ text: 'TBA Alignments', value: 'n_tba_alignments', sortable: true, class: 'k-resource-set-table-min', align: 'center' })
				arr.push({ text: 'Cross-Alignments', value: 'n_cross_alignments', sortable: true, class: 'k-resource-set-table-min', align: 'center' })

			} else {
				arr.push({ text: 'Alignments', value: 'n_alignments', sortable: true, class: 'k-resource-set-table-min', align: 'center' })
			}
			if (this.show_resource_identifiers) arr.push({ text: 'ID', value: 'external_resource_id', sortable: true, class: 'k-resource-set-table-min', align: 'left' })
			arr.push({ text: 'Created At', value: 'created_at', sortable: true, class: 'k-resource-set-table-min', align: 'center' })
			if (this.is_case_resource_set) {
				arr.push({ text: 'Alignments Updated At', value: 'alignments_last_updated_at', sortable: true, class: 'k-resource-set-table-min', align: 'center' })
			}
			return arr
		},
		n_resource_alignments() {
			console.warn('n_resource_alignments computed')
			// cache this computed to speed up processing
			if (this.tfr_compute_paused && this.cached_nra !== null) return this.cached_nra
			console.warn('n_resource_alignments computed RUNNING')

			// this.report_ms('  n_resource_alignments start')
			// do this in a separate computed so we don't have to re-do these counts every time we change filters
			let arr = []
			let n_aligned_items_to_tba_framework = 0
			let n_no_match_to_tba_framework = 0
			let total_current_tba_alignments = 0
			let total_cross_alignments = 0
			let cross_alignment_framework_records = []	// this will hold a list of all the framework_records for which we have alignments
			for (let r of this.resources_for_alignment) {
				let o = {
					n_tba_alignments: 0,
					n_cross_alignments: 0,
					n_alignments: 0,
				}
				if (this.tba_framework_identifier) {
					for (let alignment of r.alignments) {
						if (alignment.framework_identifier == this.tba_framework_identifier) {
							++o.n_tba_alignments
						} else {
							// if a base framework is specified, only count cross_alignments here for the base framework
							if (empty(this.base_framework_identifier) || this.base_framework_identifier == alignment.framework_identifier) {
								++o.n_cross_alignments
							}
							
							// but compile cross_alignment_framework_records for all alignments
							if (!cross_alignment_framework_records.find(x=>x.lsdoc_identifier == alignment.framework_identifier)) {
								let fr = this.framework_records.find(x=>x.lsdoc_identifier == alignment.framework_identifier)
								// if this is an unknown framework, add a dummy record for it now
								if (empty(fr)) {
									fr = U.create_framework_record_for_unknown_document(alignment.framework_identifier)
									this.$store.commit('set', [this.framework_records, 'PUSH', fr])
								}
								cross_alignment_framework_records.push(fr)
							}
						}
					}

					if (o.n_tba_alignments > 0) ++n_aligned_items_to_tba_framework
					total_current_tba_alignments += o.n_tba_alignments
					total_cross_alignments += o.n_cross_alignments
					if (r.no_matches?.includes(this.tba_framework_identifier)) ++n_no_match_to_tba_framework

				} else {
					o.n_alignments = r.alignments.length
				}
				arr.push(o)
			} 
			this.cross_alignment_framework_records = cross_alignment_framework_records
			this.n_aligned_items_to_tba_framework = n_aligned_items_to_tba_framework
			this.n_no_match_to_tba_framework = n_no_match_to_tba_framework
			this.total_current_tba_alignments = total_current_tba_alignments
			this.total_cross_alignments = total_cross_alignments
			// this.report_ms('  n_resource_alignments end')

			this.cached_nra = arr
			return arr
		},

		mean_sm_score() {
			let sum = 0, n = 0
			for (let resource of this.resources_for_alignment) {
				if (resource.candidates.length > 0 && !empty(resource.candidates[0].simscore)) {
					++n
					sum += resource.candidates[0].simscore
				}
			}
			if (n == 0) return null
			return (sum / n).toFixed(1)
		},

		batch_align_available() {
			// cachce this computed to speed up processing
			if (this.tfr_compute_paused && this.cached_baa !== null) return this.cached_baa

			this.cached_baa = this.resources_for_alignment?.findIndex(x=>x.candidates.length > 0) > -1
			return this.cached_baa
		},

		base_framework_options() {
			// let arr = [{value:'', text:'NONE (use alignments from all frameworks)'}]
			// let arr = [{value:'', text:'--- PLEASE CHOOSE A BASE FRAMEWORK FOR CROSS-ALIGNMENT ---'}]
			let arr = [{value:'', text:'[NO BASE FRAMEWORK SELECTED]'}]
			for (let fr of this.cross_alignment_framework_records) {
				arr.push({value: fr.lsdoc_identifier, text: U.framework_title_with_category(fr)})
			}
			return arr
		},
		items_per_page: {
			get() { return this.$store.state.lst.resource_set_list_items_per_page },
			set(val) { this.$store.commit('lst_set', ['resource_set_list_items_per_page', val]) }
		},

		item_types_set_to_default() {
			let d = this.determine_default_alignable_item_types()
			if (d.length != this.limiters.item_types.length) return false
			for (let i of this.limiters.item_types) {
				if (!d.includes(i)) return false
			}
			return true
		},

		show_tree_view_toggle() {
			// show the toggle to tree view if this is a case resource set
			if (!this.is_case_resource_set) return false
			// and we're not viewing a resource
			if (!empty(this.current_resource)) return false
			return true
		},

		outer_css() {
			// if we're not using a case resource set, background color is just #eee
			if (!this.is_case_resource_set) return {style:{'background-color':'#eee'}}
			const framework_color = U.framework_color(this.resource_set.framework_record)
			// if using a "standard" color scheme for the framework, use this class
			if (!isNaN(framework_color)) return {class: 'k-framework-color-' + framework_color}
			// else if using a custom color, this is the style we want
			return {style: U.framework_color_object(this.resource_set.framework_record, 'lighten-5')}
		},

		settings_wrapper_style() {
			let s = ''

			if (this.viewing_resource) {
				s += 'position:fixed; top:72px; z-index:1000000;'
				if (this.satchel_chooser_showing) s += 'right:520px;'
				else s += 'right:12px;'
			} else {
				s += 'margin-top:12px;'
			}

			return s
		},

		settings_style() {
			let s = 'font-size:14px; line-height:17px; width:600px; background-color:#eee; padding:12px; border-radius:10px;'
			if (this.viewing_resource) {

			} else {
				s += 'margin:0 8px 0 24px;'
			}
			// " class="ml-8 mr-2" style="" :class="tfr_compute_paused?'mb-4':''
			return s
		},

		satchel_chooser_showing() {
			return vapp.$refs.satchel.show_satchel
		},

		framework_switcher_btn_content() {
			if (!empty(this.tba_framework_identifier)) {
				return '<i class="fas fa-arrow-right-arrow-left mr-2"></i>Aligning To:'
			} else {
				return '<i class="fas fa-down-left-and-up-right-to-center mr-2" style="transform:rotate(45deg)"></i>Align To Framework…' 
			}
		},

		validation_chart_plot_type: {
			get() { return this.$store.state.lst.resource_set_validation_chart_plot_type },
			set(val) { this.$store.commit('lst_set', ['resource_set_validation_chart_plot_type', val]) }
		},

		validation_graph_order: {
			get() { return this.$store.state.lst.resource_set_validation_graph_order },
			set(val) { this.$store.commit('lst_set', ['resource_set_validation_graph_order', val]) }
		},

		validation_chart_options() {
			// return null
			if (empty(this.validation_data?.resources)) return null

			// determine fields we're plotting
			let mean_val, data_field, plot_title, y_min, y_max, y_axis_reversed = false
			if (this.validation_chart_plot_type == 'mean_highest_rank') {
				mean_val = this.validation_data.mean_highest_rank * 1
				data_field = 'highest_ranks'
				plot_title = 'Highest Rank'
				y_min = 1
				y_max = null
				y_axis_reversed = true

			} else if (this.validation_chart_plot_type == 'mean_highest_rank_score') {
				mean_val = this.validation_data.mean_highest_rank_score * 1
				data_field = 'highest_rank_scores'
				plot_title = 'Highest Rank Score'
				y_min = 0
				y_max = 100

			} else if (this.validation_chart_plot_type == 'mean_rank_score') {
				mean_val = this.validation_data.mean_rank_score * 1
				data_field = 'mean_rank_scores'
				plot_title = 'Mean Rank Score'
				y_min = 0
				y_max = 100

			} else if (this.validation_chart_plot_type == 'mean_highest_comp_score') {
				mean_val = this.validation_data.mean_highest_comp_score * 1
				data_field = 'highest_comp_scores'
				plot_title = 'Highest Semantic Match Score'
				y_min = 0
				y_max = null

			} else if (this.validation_chart_plot_type == 'mean_comp_score') {
				mean_val = this.validation_data.mean_comp_score * 1
				data_field = 'mean_comp_scores'
				plot_title = 'Mean Semantic Match Score'
				y_min = 0
				y_max = null

			} else {
				console.warn('ERROR')	// shouldn't happen
				return null
			}

			// start with the value for each resource
			let data = []
			let max_n_candidates = -1
			for (let i = 0; i < this.validation_data.resources.length; ++i) {
				let r = this.validation_data.resources[i]
				data.push({
					name: r.resource_title,
					y: this.validation_data[data_field][i],
					color: 'green',
					resource: r,
					marker: {
						radius:6,
					}
				})

				if (r.candidates.length > 0 && r.candidates.length > max_n_candidates) {
					max_n_candidates = r.candidates.length
				}
			}

			// if validation_graph_order is 'descending', sort
			if (this.validation_graph_order == 'descending') {
				data.sort((a,b)=>{
					if (this.validation_chart_plot_type == 'mean_highest_rank') {
						return a.y - b.y
					} else {
						return b.y - a.y
					}
				})
			}

			// unshift the mean value
			data.unshift({
				name: 'MEAN',
				y: mean_val,
				color: 'blue',
				marker: {
					radius:10,
				}
			})

			// if we're showing highest rank, set y_max to the max number of candidates
			if (this.validation_chart_plot_type == 'mean_highest_rank') {
				y_max = max_n_candidates
				plot_title += ` (max ${max_n_candidates})`
			}

			this.validation_chart_title = plot_title

			let o = {
				chart: {
					type: 'line',
					backgroundColor: '#eeeeee',
					height: 240,
					fontFamily: 'Roboto, sans-serif',
					events: {
						render: function() {
							// console.log('chart rendered!')
							// vapp.chart = this
						}
					},
				},
				title: { 
					text:undefined,
					// text: plot_title,
					style: { color:'#000000' },
				},
				xAxis: {
					title: plot_title,
					visible: true,
					tickLength: 0,
					labels: {
						enabled: false,
						formatter: function() {
							return this.value
						}
					},
				},
				yAxis: {
					reversed: y_axis_reversed,
					min: y_min,
					max: y_max,
					endOnTick: false,
					softMax: y_max,
					title: null,
					allowDecimals: false,
					labels: {
						style: { color:'#000000' },
					}
				},
				tooltip: {
					formatter: function() {
						let pt = vapp.resource_set_component.validation_chart_plot_type
						let d = vapp.resource_set_component.validation_chart_options.series[0].data[this.x]
						let val = this.y

						if (pt == 'mean_highest_rank') {
							if (this.x == 0) val = `${val.toFixed(1)} / ${vapp.resource_set_component.validation_data.mean_n_candidates}`
							else val = `${val} / ${d.resource.candidates.length}`

						} else if (pt == 'mean_highest_rank_score') {
							if (val != 100) val = val.toFixed(1)
						}
						return `${this.key} (${val})`
					}
				},
				plotOptions: {
				},
				legend: {
					enabled: false,
				},
				credits: { enabled: false },
				series: [{ 
					name: 'Series Name', 
					color: 'black',
					marker: { symbol: 'circle' }, 
					data: data,
					point: {
						events: {
							click: function () {
								let d = vapp.resource_set_component.validation_chart_options.series[0].data[this.x]
								vapp.resource_set_component.go_to_resource(d.resource.resource_id)
							}
						}
					}
				}],
			}

			return o
		},
	},
	watch: {
		'$vuetify.breakpoint.width': {immediate: true, handler() {
			// calculate widths for main interface and chooser
			// need 20px of padding for interface
			// min width for chooser is 460px, but we'd like to give it 600px if possible
			// we would prefer the main interface to be at least 900px
			// if width is > 1600px, just give the chooser its max width and let the main interface have the rest
			this.satchel_embed_width = Math.round((600/1600) * (this.$vuetify.breakpoint.width - 20))
			if (this.satchel_embed_width > 600) this.satchel_embed_width = 600
			if (this.satchel_embed_width < 460) this.satchel_embed_width = 460
			this.main_interface_width = this.$vuetify.breakpoint.width - 20 - this.satchel_embed_width
			// console.log(`width: ${this.main_interface_width} / ${this.satchel_embed_width} ($vuetify.breakpoint.width)`)
		}},
		current_resource: {immediate: true, handler() {
			if (empty(this.current_resource)) {
				// when current_resource goes to empty, the table has been revealed, so if we have a last_viewed_resource...
				if (!empty(this.last_viewed_resource)) {
					// make sure the proper "page" of the table is showing for the last-viewed resource
					let index = this.table_filtered_resources.findIndex(x=>x.resource.resource_id == this.last_viewed_resource.resource_id)
					if (index > -1) this.current_page = Math.floor(index / this.items_per_page) + 1

					// then scroll to the lvr in the table
					setTimeout(x=>{
						let target = $('.k-resource-set-last-viewed-resource')[0]
						if (target) this.$vuetify.goTo(target, {container:$(this.$el).find('.k-framework-list-wrapper-inner')[0], offset:0})
					}, 100)
				}
			} else {
				this.last_viewed_resource = this.current_resource
				$(this.$el).find('.k-framework-list-wrapper-inner').scrollTop(0)
			}
		}},

		table_options(val) {
			if (val.sortBy.length == 0) {
				this.table_options.sortBy = ['resource_title_index']
				this.table_options.sortDesc = false
			}
		},

		// when resources or tfr_compute_paused, or the other vars listed here change, call compute_table_filtered_resources -- which will also call compute_batch_alignments_to_create
		resources() { this.compute_table_filtered_resources() },
		tfr_compute_paused() { this.compute_table_filtered_resources() },
		viewing_resource() { this.compute_table_filtered_resources() },
		base_framework_identifier() { 
			this.compute_table_filtered_resources() 
			this.compute_all_suggestions_clean()
		},
		show_aligned_resources() { this.compute_table_filtered_resources() },
		show_unaligned_resources() { this.compute_table_filtered_resources() },
		show_no_match_resources() { this.compute_table_filtered_resources() },
		search_text() { this.compute_table_filtered_resources() },
		resource_set_branches_to_align() { this.compute_table_filtered_resources() },

		tba_framework_identifier() { 
			this.reset_all_resource_alignment_suggestions()
			this.compute_table_filtered_resources() 
			this.compute_all_suggestions_clean()
		},

		// and when these are called, we have to call compute_batch_alignments_to_create even if table_filtered_resources hasn't changed
		batch_align_available() { this.compute_batch_alignments_to_create() },
		batch_align_settings_showing() { this.compute_batch_alignments_to_create() },
		batch_min_alignments() { this.compute_batch_alignments_to_create() },
		batch_min_alignments_is_set() { this.compute_batch_alignments_to_create() },
		batch_max_alignments() { this.compute_batch_alignments_to_create() },
		batch_max_alignments_is_set() { this.compute_batch_alignments_to_create() },
		batch_simscore_threshold() { this.compute_batch_alignments_to_create() },

		$route() {
			this.set_title()
		},
		'resource_set.resource_set_title'() {
			this.set_title()
		},
	},
	async created() {
		this.report_ms('created')
		// if you're not allowed to be in the resources viewer, redirect to the framework list
		if (!this.user_can_view_this_resource_set) {
			vapp.go_to_route('frameworks')
			return
		}
		// else make sure frameworks_or_resources_toggle is set to 'resources'
		this.$store.commit('lst_set', ['frameworks_or_resources_toggle', 'resources'])

		await this.$store.dispatch('get_resource_sets')
		if (!this.resources_loaded) {
			// if the set is represented by a CASE framework...
			if (this.is_case_resource_set) {
				// load the full framework record and cfo (if not already loaded)
				await U.load_framework_and_cfo(this.resource_set.framework_record.lsdoc_identifier)
				// then load the resources from the framework record
				this.resource_set.load_resources_from_cfitems()
				this.set_resource_title_indices()

			// else load the set's resources via the get_resource_set_resources service
			} else {
				await this.$store.dispatch('get_resource_set_resources', this.resource_set_id)
				this.set_resource_title_indices()
			}
		} else {
			if (this.is_case_resource_set) {
				this.resource_set.update_resources_from_cfitems()
			}
		}

		// as soon as we launch, make sure we have the relevant framework data we need for alignments given the current settings
		await this.configure_for_tba_framework_identifier()

		// then make the initial compute_table_filtered_resources call
		this.compute_table_filtered_resources()

		vapp.resource_set_component = this
	},
	mounted() {
		this.set_title()
	},
	methods: {
		// Creating the "table_filtered_resources" array (and "batch_alignments_to_create" array, which is based on table_filtered_resources) can take a lot of processing time for large resource sets, so we have to carefully meter when these are run
		compute_table_filtered_resources() {
			if (empty(this.resources_for_alignment)) return
			// if (this.tfr_compute_paused || (this.viewing_resource && this.cached_tfr_array.length > 0)) {
			if (this.tfr_compute_paused) {
				// if (true || this.debug) console.warn(`!! tfr paused: tfr_compute_paused=${this.tfr_compute_paused} / viewing_resource: ${this.viewing_resource}`)
				this.table_filtered_resources = this.cached_tfr_array
				return
			}

			// establish the debounce fn if necessary
			if (empty(this.tfr_fn_debounced)) {
				this.tfr_fn_debounced = U.debounce(function() {
					// console.warn('table_filtered_resources computing!')
					this.report_ms('table_filtered_resources start')
					let arr = []
					for (let i = 0; i < this.resources_for_alignment.length; ++i) {
						let r = this.resources_for_alignment[i]

						// if we have a search_re, filter by it
						if (!empty(this.search_re)) {
							if (r.resource_title.search(this.search_re) == -1) continue
						}

						let o = {
							resource_index: i,
							n_cross_alignments: this.n_resource_alignments[i].n_cross_alignments,
							n_alignments: this.n_resource_alignments[i].n_alignments,
							top_candidate: null,
							top_candidate_val: -1,
							top_candidate_pct: 0,
							no_match: false,
						}

						// TODO: clean this up...
						if (false && this.batch_alignments_hash[r.resource_id]) o.n_tba_alignments = this.batch_alignments_hash[r.resource_id]?.alignments.length
						else o.n_tba_alignments = this.n_resource_alignments[i].n_tba_alignments

						// if we have a tba_framework_identifier, do some extra calculations
						if (this.tba_framework_identifier) {
							// mark no_matches
							if (r.no_matches?.includes(this.tba_framework_identifier)) {
								o.no_match = true
							}

							if (!this.show_aligned_resources && o.n_tba_alignments > 0) continue
							if (!this.show_unaligned_resources && o.n_tba_alignments == 0) continue
							if (!this.show_no_match_resources && o.no_match) continue

							// get top candidate
							if (r.candidates.length > 0) {
								o.top_candidate = r.candidates[0]
								o.top_candidate_val = r.candidates[0].simscore
								o.top_candidate_pct = r.candidates[0].simscore_pct
							}

							// TODO: maybe add this.batch_alignments_hash[r.resource_id]?.alignments.length to top_candidate_val so that items with lots of batch suggestions get listed first when we sort by top_candidate_val??
							
							// TODO: note if the suggestion is out-of-date
						}	
						
						o.resource = r
						o.resource_id = r.resource_id
						o.external_resource_id = r.external_resource_id
						o.resource_title = r.resource_title
						o.resource_title_index = r.resource_title_index
						o.created_at = r.created_at
						o.alignments_last_updated_at = r.alignments_last_updated_at ? r.alignments_last_updated_at.replace(/(.*?)T(.*)+.*/, '$1 $2') : ''
						o.icon = r.random_icon()
						o.icon_color = r.random_color()
						o.icon_style = `transform:rotate(0deg)`
						// o.icon_style = `transform:rotate(${r.random_icon_rotation()}deg)`
						o.n_alignments = r.alignments.length

						// indicate when the number of TBA alignments differs from the number of cross-alignments, if we're using a base
						o.cross_alignment_cell_class = ''
						if (this.base_framework_identifier) {
							// this would indicate that you've got more alignments to TBA than to base; I don't think we need to indicate this.
							// if (o.n_cross_alignments < o.n_tba_alignments) o.cross_alignment_cell_class = 'blue darken-3 white--text px-1'
							if (o.n_cross_alignments > o.n_tba_alignments) o.cross_alignment_cell_class = 'orange darken-3 white--text px-1'
						}
						
						arr.push(o)
					}

					this.report_ms('table_filtered_resources end')
					this.cached_tfr_array = arr
					this.table_filtered_resources = arr

					this.sort_table_filtered_resources()

					// compute_batch_alignments_to_create after table_filtered_resources is updated
					this.compute_batch_alignments_to_create()
		
				}, 10)
			}
			// call the debounce fn
			this.tfr_fn_debounced()
			this.table_filtered_resources = this.cached_tfr_array
		},

		set_resource_title_indices() {
			// this, annoyingly, needs to be done when we load, add, or change resources, to make sure the table sorting works properly
			// TODO: not sure if we will need this anymore once everything is using CASE frameworks
			this.resources.sort((a,b)=>{
				return U.natural_sort(a.resource_title, b.resource_title)
			})
			for (let i = 0; i < this.resources.length; ++i) {
				this.resources[i].resource_title_index = i
			}
		},

		sort_table_filtered_resources() {
			console.log(`sorting by ${this.table_sort_by} - desc ${this.table_sort_desc}`)
			this.table_filtered_resources.sort((a,b)=>{
				// let x = U.natural_sort(a[this.table_sort_by]+'', b[this.table_sort_by]+'')
				let x = (a[this.table_sort_by] < b[this.table_sort_by]) ? -1 : (a[this.table_sort_by] > b[this.table_sort_by]) ? 1 : 0
				if (this.table_sort_desc) x *= -1
				return x
			})		
		},

		compute_batch_alignments_to_create() {
			if (this.tfr_compute_paused) return this.cached_batc

			if (!this.batch_align_available || !this.batch_align_settings_showing) {
				this.n_batch_alignments_to_create = 0
				this.batch_alignments_hash = {}
				return []
			}

			// establish the debounce fn if necessary
			if (empty(this.compute_batch_alignments_to_create_debounced)) {
				this.compute_batch_alignments_to_create_debounced = U.debounce(function() {
					this.report_ms('batch_alignments_to_create start')
					// console.warn('batch_alignments_to_create start')
					let n_total_alignments = 0
					let resources_to_save = []
					let hash = {}
					for (let row of this.table_filtered_resources) {
						let resource = row.resource						
						let r = {
							resource_id: resource.resource_id,
							alignments: [],
						}
						for (let candidate of resource.candidates) {
							// if ((this.batch_max_alignments != -1 && r.alignments.length >= this.batch_max_alignments) || candidate.simscore < this.batch_simscore_threshold) break
							// if max alignments is set and we've got that many alignments already, break
							if (this.batch_max_alignments != -1 && r.alignments.length >= this.batch_max_alignments) {
								break
							}
							// if we've reached a candidate whose simscore < the threshold...
							if (candidate.simscore < this.batch_simscore_threshold) {
								// if min alignments isn't set, or we've got that many alignments already, break
								if (this.batch_min_alignments == -1 || r.alignments.length >= this.batch_min_alignments) {
									break
								}
							}
							// if we get to here, add the alignment if it's not already aligned
							if (candidate.currently_aligned) continue
							r.alignments.push({
								fi: this.tba_framework_identifier,
								ii: candidate.cfitem.identifier,
							})
							// TODO: deal with simscore ties??
						}
						if (r.alignments.length > 0) {
							resources_to_save.push(r)
							n_total_alignments += r.alignments.length
							hash[resource.resource_id] = r
						}
					}
					this.n_batch_alignments_to_create = n_total_alignments
					this.batch_alignments_hash = hash
					this.report_ms('batch_alignments_to_create end')

					this.cached_batc = resources_to_save
					this.batch_alignments_to_create = resources_to_save
				}, 200)
				// TODO: we could use a larger delay for bigger sets...
			}
			// call the debounce fn
			this.compute_batch_alignments_to_create_debounced()
		},

		// calculate if we currently need to update any suggestions
		compute_all_suggestions_clean() {
			if (empty(this.resources_for_alignment)) return
			if (this.tfr_compute_paused) return

			// establish the debounce fn if necessary
			if (empty(this.casc_fn_debounced)) {
				this.casc_fn_debounced = U.debounce(function() {
					this.all_suggestions_clean = true
					for (let o of this.table_filtered_resources) {
						if (this.all_suggestions_clean && this.resource_suggestion_dirty(o.resource)) {
							this.all_suggestions_clean = false
							return
						}
					}
				}, 10)
			}
			// call the debounce fn
			this.casc_fn_debounced()
		},

		// we need to keep track of table pagination for the current_resource scrolling to work (see above)
		pagination_changed(o) {
			// console.warn('pagination_changed', o)
			this.items_per_page = o.itemsPerPage
			this.current_page = o.page
		},

		async select_branches() {
			if (this.viewing_resource) {
				vapp.resource_viewer_component.auto_starting_alignment = false
			}

			let data = {
				framework_identifier: this.tba_framework_identifier,
				allow_framework_switching: false	// don't allow framework switching here
			}
			let show_data = {
				window_side: (!this.viewing_resource ? 'left' : 'right'),
				// fn called when embedded satchel is hidden
				embed_hide_callback_fn: (arg)=>{ 
					this.selecting_branches = false
				},
			}

			let selected_items = this.limiters.include_branches.concat([])
			if (selected_items.length > 0) {
				data.selected_items = selected_items
			}

			this.selecting_branches = true

			await vapp.$refs.satchel.execute('show', show_data)
			await vapp.$refs.satchel.execute('load_framework', data)
			vapp.$refs.satchel.execute('chooser', {chooser_mode: true}).then((aligned_item) => {
				// console.log(aligned_item)
				// if user previously chose this item, remove it
				let i = selected_items.findIndex(identifier=>identifier==aligned_item.cfitem.identifier)
				if (i > -1) {
					selected_items.splice(i, 1)

				} else {
					// else add it
					selected_items.push(aligned_item.cfitem.identifier)
				}

				// set param and save match_params
				this.limiters.include_branches = selected_items
				this.limiters_updated()

				// re-initialize the chooser to show the updated choices
				this.select_branches()
			})
		},

		remove_branch(identifier) {
			let selected_items = this.limiters.include_branches.concat([])
			let index = selected_items.findIndex(x=>x == identifier)
			selected_items.splice(index, 1)
			this.limiters.include_branches = selected_items
		},

		stop_selecting_branches() {
			if (this.selecting_branches) {
				this.selecting_branches = false
				vapp.$refs.satchel.execute('hide')
			}
		},

		// this is called when the user closes the alignment settings when viewing a resource
		close_settings_panel() {
			this.stop_selecting_branches()
			this.settings_showing = false
		},
		
		get_item_type_example(item_type) {
			let keys = Object.keys(this.tba_framework_record.cfo.cfitems)
			U.shuffle_array(keys)
			for (let key of keys) {
				let cfi = this.tba_framework_record.cfo.cfitems[key]
				if (U.item_type_string(cfi) == ((item_type == '[no item type]') ? '' : item_type)) {
					this.$set(this.item_type_examples, item_type, U.generate_cfassociation_node_uri_title(cfi, 150, true))
					return
				}
			}
			this.$set(this.item_type_examples, item_type, '???')
		},

		handle_search() {
			// establish the debounce fn if necessary
			if (empty(this.handle_search_debounced)) {
				this.handle_search_debounced = U.debounce(() => {
					this.search_text = $.trim(this.search_text)
					if (empty(this.search_text)) {
						this.search_re = null
					} else {
						this.search_re = new RegExp(this.search_text, 'i')
					}
					// re-compute table_filtered_resources when search changes
					this.compute_table_filtered_resources()
				}, 500)
			}
			// call the debounce fn
			this.handle_search_debounced()
		},
		formatted_date(s) {
			if (!s) return '–'
			s += '+00:00'	// add GMT timezone indicator, then use U.local_last_change_date_time
			return U.local_last_change_date_time(s)

			// console.log(s)
			// if (!s) return '–'
			// s = s.replace(/(.*?)T(.*)+.*/, '$1 $2')
			// // add GMT timezone indicator if needed, then use U.local_last_change_date_time
			// // if (!s.includes('+00:00')) {
			// 	s += '+00:00'
			// // }
		},

		candidate_color(resource, candidate) {
			if (candidate?.currently_aligned) return 'orange darken-3'
			if (this.batch_align_settings_showing) {
				if (this.batch_alignments_to_create.find(x=>x.resource_id==resource.resource_id)) return 'pink darken-3'
			}
			return 'grey darken-4'
		},

		rename_set() {
			this.$prompt({
				title: 'Rename Resource Set',
				text: 'Enter the new name for this resource set:',
				initialValue: this.resource_set.resource_set_title,
				disableForEmptyValue: true,
				acceptText: 'Save',
				acceptIcon: 'fas fa-save',
			}).then(title => {
				title = $.trim(title)
				if (empty(title)) return
				this.$store.commit('set', [this.resource_set, 'resource_set_title', title])

				let payload = {
					service_url: 'save_resource_set',
					user_id: this.user_info.user_id,
					resource_set_id: this.resource_set.resource_set_id,
					resource_set_data: {resource_set_title: this.resource_set.resource_set_title},
				}
				U.loading_start()
				this.$store.dispatch('service', payload).then((result)=>{
					U.loading_stop()
					if (!result.resource_set) {
						console.log(result)
						this.$alert('save_resource_set service failed')
					}
				}).catch(result=>{
					U.loading_stop()
					this.$alert('Error: ' + result?.status)
				})

			}).catch(n=>{console.log(n)}).finally(f=>{})
		},

		add_set_editor() {
			this.$prompt({
				title: 'Add an Editor',
				text: 'Enter the email address for the person to make an editor for this Resource Set:',
				promptType: 'autocomplete',
				serviceName: 'email_search',
				initialValue: '',
				acceptText: 'Add Editor',
			}).then(email => {
				// return value should be e.g. 'pepper@gmail.com (Pepper Williams)'; extract the email
				email = $.trim(email).toLowerCase().replace(/^(\S+).*/, '$1')

				// if email is already in editor_emails, tell them
				if (this.resource_set.editor_emails.includes(email)) {
					this.$alert('That user is already an editor for this Resource Set.')
					return
				}

				// else add to editor_emails
				this.$store.commit('set', [this.resource_set.editor_emails, 'PUSH', email])

				let payload = {
					service_url: 'save_resource_set',
					user_id: this.user_info.user_id,
					resource_set_id: this.resource_set.resource_set_id,
					resource_set_data: {editor_emails: this.resource_set.editor_emails},
				}
				U.loading_start()
				this.$store.dispatch('service', payload).then((result)=>{
					U.loading_stop()
					if (!result.resource_set) {
						console.log(result)
						this.$alert('save_resource_set service failed')
					}
				}).catch(result=>{
					U.loading_stop()
					this.$alert('Error: ' + result?.status)
				})

			}).catch(n=>{console.log(n)}).finally(f=>{})
		},

		go_to_resource(which, $event) {
			if (!this.resources_for_alignment) return	// shouldn't happen
			let current_resource_index = this.table_filtered_resources.findIndex(x=>x.resource_id == this.resource_id)

			if (which == 'prev') {
				if (current_resource_index > 0) which = this.table_filtered_resources[current_resource_index - 1].resource_id
			} else if (which == 'next') {
				if (current_resource_index < this.table_filtered_resources.length-1) which = this.table_filtered_resources[current_resource_index + 1].resource_id
			}

			let url = `/resources/${this.resource_set_id}/${which}`
			this.$router.push({ path: url })

			// if (typeof(which) == 'number') {
			// 	let url = `/resources/${this.resource_set_id}/${which}`
			// 	this.$router.push({ path: url })
			// } else {	// shouldn't happen
			// 	console.error('go_to_resource: bad value for which: ', which)
			// }
		},

		show_table(flag) {
			// if the user is editing or creating a new resource, ask ResourceEdit to check for changes
			if (flag !== 'confirmed' && this.$refs.resource_viewer?.editing_resource) {
				this.$refs.resource_viewer.edit_resource_cancel('show_table')
				return
			}
			if (this.resource_id == 1) {
				// if they filled anything in, ask if they want to save
				// otherwise kill that resource
				let i = this.resources.findIndex(x=>x.resource_id == 1)
				if (i > -1) {
					this.$store.commit('set', [this.resources, 'SPLICE', i])
				}
			}
			let url = `/resources/${this.resource_set_id}`
			this.$router.push({ path: url })
		},

		create_new_resource() {
			if (this.$refs.resource_viewer?.editing_resource) {
				this.$alert('Please finish editing the current resource (or cancel) before creating a new resource.')
				return
			}

			// actual resource_id's start at 1000, so we'll know that resource_id 1 needs to be saved
			let r = new Resource({
				resource_id: 1, 
				identifier: U.new_uuid(),	// this will be used if we're using a case resource, ignored otherwise
				resource_set_id: this.resource_set_id, 
				creator_user_id: this.user_info.user_id
			}, this.resource_set.framework_record)	// resource_set.framework_record will be empty if this is an "OG" resource
			this.$store.commit('set', [this.resources, 'PUSH', r])
			this.go_to_resource(1)
		},

		new_resource_created(resource_data) {
			let i = this.resources.findIndex(x=>x.resource_id == 1)
			if (i > -1) {	// should always be true
				let r = new Resource(resource_data)
				this.$store.commit('set', [this.resources, 'SPLICE', i, r])

				// set the resource_title_index for the new resource, then make sure we're sorted properly
				this.set_resource_title_indices()
				this.sort_table_filtered_resources()

				this.go_to_resource(r.resource_id)
			}
		},

		resource_removed(resource_id) {
			this.show_table('confirmed')
			this.$nextTick(x=>{
				let index = this.resource_set.resources.findIndex(x=>x.resource_id == resource_id)
				if (index == -1) this.$alert('ERROR: couldn’t find index of resource in resource_set')	// shouldn't happen
				else this.$store.commit('set', [this.resource_set.resources, 'SPLICE', index])
			})
		},

		check_for_base_framework_required() {
			// currently, at least, we require a base framework to make suggestions, if cross alignments are being considered
			// (this is in part because if you've got a big resource set and/or a lot of cross-alignments, it can take a really long time to load all the necessary data for cross-alignments from a lot of frameworks)

			// if we're not using cross-alignments as a source of evidence, we don't need to do this
			if (!this.comp_factor_assocs) return true

			// if we have specified a base framework, we're good
			if (this.base_framework_identifier) return true

			// if we don't actually have any cross alignments, we're also good
			if (this.cross_alignment_framework_records.length == 0) return true

			// if we only have alignments from one other framework, just select it
			if (this.cross_alignment_framework_records.length == 1) {
				this.base_framework_identifier = this.cross_alignment_framework_records[0].lsdoc_identifier
				return true
			}

			// if we get to here we do need a base framework specified and it isn't specified, so show an alert and return false
			this.$alert('To use cross-alignments as a basis for new alignment suggestions, please specify a “base framework”: the framework that includes the cross-alignments you want to use as evidence for suggestions.')
			return false
		},

		get_all_resource_alignment_suggestions() {
			if (this.all_suggestions_clean) return
			if (this.limiters.item_types.length == 0) {
				this.$alert('To compute suggestions, you must choose one or more item types to align to.')
				return
			}

			if (!this.check_for_base_framework_required()) return

			// reset validation stuff when this is clicked
			this.validation_data = null
			this.show_validation_data = true

			let arr = this.table_filtered_resources
			this.suggestions_to_compute = arr.length
			this.suggestions_computed = 0

			// reset all dirty suggestions before we start
			this.reset_all_resource_alignment_suggestions('dirty_only')

			// start suggesting; suggest will re-call itself as long as it has more suggestions to compute and the user doesn't click to stop
			this.tfr_compute_paused = true
			this.suggest(arr, 0)
		},

		async suggest(arr, index) {
			let resource = arr[index].resource
			// console.warn(resource.resource_title)

			// skip resources that don't need to be updated
			while (this.suggestions_to_compute > 0 && resource && !this.resource_suggestion_dirty(resource)) {
				++index
				resource = arr[index].resource
				--this.suggestions_to_compute
			}
			// if that got us to the end of the list, we're done
			if (this.suggestions_to_compute == 0) {
				this.tfr_compute_paused = false
				return
			}

			await this.get_resource_alignment_suggestions(resource)
			--this.suggestions_to_compute
			++this.suggestions_computed

			if (this.suggestions_to_compute > 0) {
				setTimeout(x=>this.suggest(arr, index + 1))
				// if we're not pausing computation, update the table after every X suggestions are computed, where X depends on the # of resources we have
				if (!this.tfr_compute_paused) {
					let update_after
					if (this.resources_for_alignment.length <= 20) update_after = 1
					else if (this.resources_for_alignment.length <= 50) update_after = 4
					else update_after = 8

					if (this.suggestions_computed % update_after == 0) {
						this.tfr_compute_paused = false
						this.compute_table_filtered_resources()
						this.tfr_compute_paused = true
					}
				}

			} else {
				this.tfr_compute_paused = false
				this.compute_all_suggestions_clean()
			}
		},

		reset_all_resource_alignment_suggestions(flag) {
			for (let resource of this.resources) {
				if (flag != 'dirty_only' || this.resource_suggestion_dirty(resource)) {
					resource.candidate_params_string = null
					resource.candidates = []
				}
			}
		},

		cancel_get_all_suggestions() {
			this.suggestions_to_compute = -1
			this.tfr_compute_paused = false
		},

		batch_align_start_clicked($event) {
			// hidden functionality: hold shift key down while clicking batch btn to batch-unalign
			if ($event.shiftKey) {
				this.batch_unalign_all()
				return
			}

			this.batch_align_settings_showing = true
		},

		async batch_unalign_all() {
			let arr = []
			let n = 0
			for (let resource of this.resources_for_alignment) {
				if (resource.alignments.length > 0) {
					let rdata = {
						resource_id: resource.resource_id,
						alignments: []
					}
					let found_one = false
					for (let alignment of resource.alignments) {
						// we only clear alignments from the tba framework
						if (alignment.framework_identifier != this.tba_framework_identifier) continue

						// CASE way or OG way
						if (this.is_case_resource_set) {
							arr.push(alignment.cfassociation)
						} else {
							rdata.alignments.push({clear: alignment.resource_alignment_id})
						}
						++n
						found_one = true
					}
					if (found_one && !this.is_case_resource_set) arr.push(rdata)
				}
			}

			console.log(arr)
			if (n == 0) {
				this.$alert('No alignments to clear.')
				return
			}

			try {
				await this.$confirm({
					title: 'Are you sure?',
					text: `Are you sure you want to clear ${n==1?'the 1 alignment':'the ' + n + ' alignments'} to this framework (${this.tba_framework_record.json.CFDocument.title})?`,
					acceptText: ((n == 1) ? 'Clear Alignment' : 'Clear Alignments'),
					acceptColor: 'red darken-3',
					acceptIcon: 'fas fa-xmarks-lines',
				})
			} catch(e) { return }

			// OG way
			if (!this.is_case_resource_set) {
				let payload = {
					service_url: 'save_resources',
					resources: JSON.stringify(arr),
					user_id: this.user_info.user_id,
					return_resources: 'no',
					return_resource_alignments: 'no',
					return_raw_alignments: 'no',
				}
				U.loading_start()
				this.$store.dispatch('service', payload).then((result)=>{
					U.loading_stop()

					// remove alignments from the resources
					for (let rdata of arr) {
						let resource = this.resources_for_alignment.find(x=>x.resource_id == rdata.resource_id)
						for (let o of rdata.alignments) {
							let i = resource.alignments.findIndex(x=>x.resource_alignment_id == o.clear)
							let alignment = resource.alignments[i]
							if (i > -1) this.$store.commit('set', [resource.alignments, 'SPLICE', i])

							// also if the resource has candidates and this item is one of them, mark it as not currently aligned
							let candidate = resource.candidates.find(x=>x.cfitem.identifier == alignment?.item_identifier)
							if (candidate) this.$store.commit('set', [candidate, 'currently_aligned', false])
						}
					}

				}).catch((result)=>{
					U.loading_stop()
					this.$alert('<b class="red--text">An error occurred:</b> ' + (result.error ? result.error : result.status))
				}).finally(()=>{})

			// CASE way
			} else {
				// remove CFAssociations, following pattern from CASEFrameworkViewer.remove_association_finish
				// arr should already include the associations to remove
				// for now at least we assume that all assocs in items_to_clear are either in the resource set's framework or in a crosswalk framework; if in the resource framework...
				if (this.resource_set.framework_record.json.CFAssociations.find(x=>x.identifier == arr[0].identifier)) {
					let payload = {
						framework_record: this.resource_set.framework_record,
						associations_to_delete: arr,
						check_out_and_in: 'yes',		// require check_out_and_in; this saves an archive and prevents this from being done if someone else is editing the framework
						update_document_date: 'no',		// don't change the document date
					}
					// note that delete_associations will call the fn to update association counts
					this.$store.dispatch('delete_associations', payload).then(x=>{
						// remove alignments from their resources
						for (let assoc of arr) {
							// get the resource being updated
							let resource = this.resource_set.resources.find(x=>x.resource_id == assoc.originNodeURI.identifier)

							let i = resource.alignments.findIndex(x=>x.resource_alignment_id == assoc.identifier)
							if (i > -1) {	// should always be true
								this.$store.commit('set', [resource.alignments, 'SPLICE', i])

								// also if the resource has candidates and this item is one of them, mark it as not currently aligned
								let candidate = resource.candidates.find(x=>x.cfitem.identifier == assoc.destinationNodeURI.identifier)
								if (candidate) this.$store.commit('set', [candidate, 'currently_aligned', false])
							}
						}

						// update table
						this.compute_table_filtered_resources()
					})
				} else {
					console.warn('ERROR: clear_alignments')
					// TODO: support crosswalk frameworks
				}
			}
		},

		async do_batch_alignments() {
			if (this.n_batch_alignments_to_create == 0) return

			try {
				await this.$confirm({
					title: 'Are you sure?',
					text: `Are you sure you want to batch-create ${this.n_batch_alignments_to_create==1?'this alignment':'these ' + this.n_batch_alignments_to_create + ' alignments'}?`,
					acceptText: ((this.n_batch_alignments_to_create == 1) ? 'Create Alignment' : 'Create Alignments'),
					acceptColor: 'pink darken-3',
					acceptIcon: 'fas fa-wand-magic-sparkles',
				})
			} catch(e) { return }

			// OG way
			if (!this.is_case_resource_set) {
				let payload = {
					service_url: 'save_resources',
					resources: JSON.stringify(this.batch_alignments_to_create),
					user_id: this.user_info.user_id,
					return_resources: 'no',
					return_resource_alignments: 'no',
					return_raw_alignments: 'yes',
				}
				U.loading_start()
				this.$store.dispatch('service', payload).then((result)=>{
					U.loading_stop()

					// add alignments to the resources, based on new_alignments we get back from the service
					for (let new_alignment of result.new_alignments) {
						let ra = new Resource_Alignment(new_alignment)
						let resource = this.resources_for_alignment.find(x=>x.resource_id == ra.resource_id)
						if (!resource) {	// shouldn't happen
							console.error('do_batch_alignments: couldn’t find resource ' + ra.resource_id)
							continue
						}
						this.$store.commit('set', [resource.alignments, 'PUSH', ra])

						// also if the resource has candidates and this aligned item is one of them, mark it as currently aligned
						let candidate = resource.candidates.find(x=>x.cfitem.identifier == new_alignment.item_identifier)
						if (candidate) this.$store.commit('set', [candidate, 'currently_aligned', true])
					}

					this.$alert({title: 'Done', text:'Batch alignment complete!'})
					this.compute_table_filtered_resources()

				}).catch((result)=>{
					U.loading_stop()
					this.$alert('<b class="red--text">An error occurred:</b> ' + (result.error ? result.error : result.status))
				}).finally(()=>{})

			// CASE way
			} else {
				// TODO: support crosswalk frameworks; for now we're saving in the resource framework
				let association_framework_record
				if (true || !this.save_to_crosswalk_framework) {
					association_framework_record = this.resource_set.framework_record
				} else {
					// copy from ResourceAlignmentMixin.make_alignments
				}
	
				let data = {
					lsdoc_identifier: association_framework_record.lsdoc_identifier,
					CFAssociations: [],
					check_out_and_in: 'yes',		// require check_out_and_in; this saves an archive and prevents this from being done if someone else is editing the framework
					update_document_date: 'no',		// don't change the document date
				}

				for (let ro of this.batch_alignments_to_create) {
					let resource = this.resource_set.resources.find(x=>x.resource_id == ro.resource_id)
					for (let ra of ro.alignments) {
						// try to get aligned_item; this should work
						let aligned_cfitem = this.tba_framework_record.cfo.cfitems[ra.ii]
						if (!aligned_cfitem) {
							console.error('couldn’t get aligned item', ra)
							continue
						}

						// construct association object
						let assoc = {
							// left node is the resource
							originNodeURI: {
								title: `${resource.resource_title} (:${this.resource_set.resource_set_id}:)`,
								identifier: resource.resource_id,
								uri: U.generate_child_uri(this.resource_set.framework_record.json.CFDocument, resource.resource_id, 'CFItems'),
							},

							// right_node_uri is the aligned item
							destinationNodeURI: {
								title: U.generate_cfassociation_node_uri_title(aligned_cfitem) + ` (:${ra.fi}:)`,
								identifier: aligned_cfitem.identifier,
								uri: aligned_cfitem.uri,
							},

							associationType: 'ext:isAlignedTo',
						}

						// fill in other parts of the CFAssociation object
						assoc = new CFAssociation(assoc)
						assoc.complete_data(association_framework_record.json.CFDocument)
						assoc = assoc.to_json()

						data.CFAssociations.push(assoc)
					}
				}
				// if we're saving to the crosswalk framework, send the document in case this is the first time we're saving the crosswalk framework
				// TODO: need to check this when we support crosswalk frameworks - match make_alignments
				if (false && this.save_to_crosswalk_framework) {
				}

				data.show_spinner = true
				this.$store.dispatch('save_framework_data', data).then(()=>{
					// update timestamps with the value sent back from the server, then push to store
					for (let assoc of data.CFAssociations) {
						assoc.lastChangeDateTime = this.$store.state.framework_lastChangeDateTime
						this.$store.commit('set', [association_framework_record.json.CFAssociations, 'PUSH', assoc])

						// also update cfo.associations_hash
						U.update_associations_hash(association_framework_record.cfo, assoc)

						// get the resource being updated
						let resource = this.resource_set.resources.find(x=>x.resource_id == assoc.originNodeURI.identifier)

						// add alignment to the resource
						this.$store.commit('set', [resource.alignments, 'PUSH', new Resource_Alignment(assoc)])

						// also if the resource has candidates and this aligned item is one of them, mark it as currently aligned
						let candidate = resource.candidates.find(x=>x.cfitem.identifier == assoc.destinationNodeURI.identifier)
						if (candidate) this.$store.commit('set', [candidate, 'currently_aligned', true])

						// update table
						this.compute_table_filtered_resources()
					}
				})
			}
		},

		async select_branches_for_alignment() {
			let fr = this.resource_set.framework_record
			let data = {
				framework_identifier: fr.lsdoc_identifier,
				allow_framework_switching: false	// don't allow framework switching here
			}
			let show_data = {
				window_side: 'right',
				// fn called when embedded satchel is hidden
				embed_hide_callback_fn: (arg)=>{ 
					this.selecting_resource_set_branches_to_align = false
				},
			}

			let selected_items = this.resource_set_branches_to_align.concat([])
			if (selected_items.length > 0) {
				data.selected_items = selected_items
			}

			this.selecting_resource_set_branches_to_align = true

			await vapp.$refs.satchel.execute('show', show_data)
			await vapp.$refs.satchel.execute('load_framework', data)
			vapp.$refs.satchel.execute('chooser', {chooser_mode: true}).then((aligned_item) => {
				// console.log(aligned_item)
				// if user previously chose this item, remove it
				let i = selected_items.findIndex(identifier=>identifier==aligned_item.cfitem.identifier)
				if (i > -1) {
					selected_items.splice(i, 1)

				} else {
					// else add it
					selected_items.push(aligned_item.cfitem.identifier)
				}

				// set param and save match_params
				this.resource_set_branches_to_align = selected_items
				this.limiters_updated()

				// re-initialize the chooser to show the updated choices
				this.select_branches_for_alignment()
			})
		},

		remove_branch_for_alignment(identifier) {
			let selected_items = this.resource_set_branches_to_align.concat([])
			let index = selected_items.findIndex(x=>x == identifier)
			selected_items.splice(index, 1)
			this.resource_set_branches_to_align = selected_items
		},

		stop_selecting_branches_for_alignment() {
			if (this.selecting_resource_set_branches_to_align) {
				this.selecting_resource_set_branches_to_align = false
				vapp.$refs.satchel.execute('hide')
			}
		},

		cache_suggestions() {
			// this would be for caching suggestions in localstorage so we don't have to compute when we come back...
			let hash = {}
			for (let resource of this.resources_for_alignment) {
				if (resource.candidates.length > 0 && !this.resource_suggestion_dirty(resource)) {
					let arr = []
					for (let candidate of resource.candidates) {
						arr.push({
							id: candidate.cfitem.identifier,
							comps: candidate.comps,
							simscore: candidate.simscore,
						})
						break
					}
					hash[resource.resource_id] = arr 
				}
			}
			let s = JSON.stringify(hash)
			console.warn('cache', s.length)
		},

		// vapp.resource_set_component.candidates_to_show_params.max_in_memory=1000
		// K-8:
		// vapp.resource_set_component.limiters.include_branches = ['26b40784-0d5e-11eb-bb8c-0242ac150004', '38609696-0d5e-11eb-a666-0242ac150004', '32f19f5c-9d63-11e7-a0b8-71e52e5326e7', 'fc4bbf16-9d60-11e7-bfe2-d75343e851d9', 'fc14bf8e-9d60-11e7-a730-f2791a4efa0e', '21d190c3-aac1-4d96-b9e1-a388cee71ff9']; vapp.resource_set_component.candidates_to_show_params.max_in_memory=1000
		// HS:
		// vapp.resource_set_component.limiters.include_branches = ['32f19f5c-9d63-11e7-a0b8-71e52e5326e7', 'fc4bbf16-9d60-11e7-bfe2-d75343e851d9', 'fc14bf8e-9d60-11e7-a730-f2791a4efa0e', '21d190c3-aac1-4d96-b9e1-a388cee71ff9']; vapp.resource_set_component.candidates_to_show_params.max_in_memory=1000
		// vapp.resource_set_component.validate_alignments()
		validate_alignments() {
			let stats = {
				resources: [],
				n_candidates: [],
				highest_suggestion_top_3: [],
				highest_ranks: [],
				highest_rank_scores: [],
				mean_rank_scores: [],
				highest_comp_scores: [],
				mean_comp_scores: []
			}
			// for each resource...
			for (let resource of this.resources_for_alignment) {
				// if the resource has candidates... (if for some reason we couldn't get any candidates, we can't validate anything)
				if (resource.candidates.length > 0) {
					let highest_comp_score = -1, sum_comp_scores = 0, n_matches = 0, indices = [], has_tba_alignment = false
					// for each alignment to the tba_framework_identifier...
					for (let alignment of resource.alignments) {
						if (alignment.framework_identifier == this.tba_framework_identifier) {
							has_tba_alignment = true

							// find the `candidate` object that goes with this aligned cfitem, if there is one
							for (let i = 0; i < resource.candidates.length; ++i) {
								let c = resource.candidates[i]
								if (c.cfitem.identifier == alignment.item_identifier) {
									indices.push(i)
									let comp_score = c.simscore
									if (comp_score > highest_comp_score) highest_comp_score = comp_score
									sum_comp_scores += comp_score
									++n_matches
									// console.log(`Match candidate ${i+1}: ${resource.resource_title} - ${c.cfitem.humanCodingScheme} (${comp_score})`)
								}
							}
						}
					}

					// skip resources that don't have any TBA alignments -- there's nothing to validate in this case
					if (!has_tba_alignment) continue

					// also, if the # of candidates is < candidates_to_show_params.max_in_memory, skip if there were no matching candidates -- nothing to validate in this case either
					// (if we have max_in_memory candidates, we might not have candidates because some resources' "true" alignments were lower than max_in_memory in the list)
					if (resource.candidates.length < this.candidates_to_show_params.max_in_memory) {
						if (n_matches == 0) continue
					}

					stats.resources.push(resource)
					stats.n_candidates.push(resource.candidates.length)

					if (highest_comp_score == -1) {
						// we only store a limited number of candidates in memory; if this happens, none of the established TBA alignments was one of the candidates
						stats.highest_ranks.push(resource.candidates.length + 1)
						stats.highest_rank_scores.push(0)
						stats.mean_rank_scores.push(0)
						stats.highest_comp_scores.push(0)
						stats.mean_comp_scores.push(0)

					} else {
						// calculate 0-100 "rank_score" values indicating how close to perfect the LLM was to matching the SME
						// suppose there are 10 candidates and 3 alignments, which matched the first, second, and fifth candidates (indices 0, 1, and 4)
						// for the first alignment, ideally it would have been first out of 10 and it was, so its rank score is (100 - ((0 - 0) / 10 * 100)) = 100
						// for the second alignment, ideally it would have beeen first out of the remaining 9 and it was, so its rank score is (100 - ((1 - 1) / 9 * 100)) = 100
						// for the third alignment, ideally it would have been first out of the remaining 8; its rank score is (100 - ((4 - 2) / 8 * 100)) = 75
						indices.sort((a,b)=>a-b)
						let denom = resource.candidates.length
						let highest_rank_score = 100 - (indices[0] / denom * 100)
						// console.log(highest_rank_score, denom, indices)
						
						let sum__mean_rank_scores = 0
						for (let i = 0; i < indices.length; ++i) {
							// console.log(i, indices[i], (100 - ((indices[i] - i) / denom * 100)))
							sum__mean_rank_scores += (100 - ((indices[i] - i) / denom * 100))
							--denom
						}

						stats.highest_suggestion_top_3.push(indices[0] <= 2 ? 1 : 0)
						stats.highest_ranks.push(indices[0] + 1)
						stats.highest_rank_scores.push(highest_rank_score)
						stats.mean_rank_scores.push(Math.round(sum__mean_rank_scores / n_matches * 10) / 10)
						stats.highest_comp_scores.push(highest_comp_score)
						stats.mean_comp_scores.push(Math.round(sum_comp_scores / n_matches))
					}
				}
			}
			console.log(stats)

			if (stats.resources.length == 0) {
				this.$alert('No resources with candidates to validate.')
				this.validation_data = null
				this.show_validation_data = true
				return
			}

			let sum_highest_suggestion_top_3 = 0, sum_highest_ranks = 0, sum_highest_rank_scores = 0, sum_highest_comp_scores = 0, sum_mean_rank_scores = 0, sum_comp_scores = 0, sum_n_candidates = 0
			for (let i = 0; i < stats.resources.length; ++i) {
				// sum_highest_rank_scores += (100 - (stats.highest_rank_scores[i] / stats.n_candidates[i] * 100))
				// sum_mean_rank_scores += (100 - (stats.mean_rank_scores[i] / stats.n_candidates[i] * 100))

				sum_highest_suggestion_top_3 += stats.highest_suggestion_top_3[i]
				sum_highest_ranks += stats.highest_ranks[i]
				sum_highest_rank_scores += stats.highest_rank_scores[i]
				sum_mean_rank_scores += stats.mean_rank_scores[i]

				sum_highest_comp_scores += stats.highest_comp_scores[i]
				sum_comp_scores += stats.mean_comp_scores[i]
				
				sum_n_candidates += stats.n_candidates[i]
			}
			stats.mean_highest_suggestion_top_3 = Math.round(sum_highest_suggestion_top_3 / stats.resources.length * 100)
			stats.mean_highest_rank = (sum_highest_ranks / stats.resources.length).toFixed(1)
			stats.mean_highest_rank_score = (sum_highest_rank_scores / stats.resources.length).toFixed(1)
			stats.mean_rank_score = (sum_mean_rank_scores / stats.resources.length).toFixed(1)

			stats.mean_highest_comp_score = Math.round(sum_highest_comp_scores / stats.resources.length)
			stats.mean_comp_score = Math.round(sum_comp_scores / stats.resources.length)
			
			stats.mean_n_candidates = Math.round(sum_n_candidates / stats.resources.length)

			// console.log(`n_resources	mean_high_rank	mean_high_rank_score	mean_rank_score	high_cs	mean_cs	mean_candidates\n${stats.resources.length}	${mean_highest_rank}	${mean_highest_rank_score}	${mean_rank_score}	${mean_highest_comp_score}	${mean_comp_score}	${mean_n_candidates}`)
			
			this.validation_data = stats
			this.show_validation_data = true
		},

		copy_validation_data() {
			U.copy_to_clipboard(`${this.validation_data.resources.length}	${this.validation_data.mean_highest_suggestion_top_3}	${this.validation_data.mean_highest_rank}	${this.validation_data.mean_highest_rank_score}	${this.validation_data.mean_rank_score}	${this.validation_data.mean_highest_comp_score}	${this.validation_data.mean_comp_score}	${this.validation_data.mean_n_candidates}`)
		},

		copy_validation_headers() {
			U.copy_to_clipboard(`# of Resources	High Rank in Top 3 (%)	Mean High Rank	Mean High Rank Score	Mean Rank Score	High SM Score	Mean SM Score	Mean # Candidates`)
		},

		go_to_tree_view() {
			vapp.go_to_route(`${this.resource_set.framework_record.lsdoc_identifier}/${this.resource_set.framework_record.lsdoc_identifier}`)
		},

		set_title() {
			const title = this.resource_set?.resource_set_title || 'Resource List';
			document.title = `${title}`;
		},
	}
}
</script>

<style lang="scss">
.k-resource-set-wrapper {
	transition: all 0.25s;
	margin:0 auto;

	.k-framework-list-wrapper-inner {
		max-height: calc(100vh - 128px);
	}
}

.k-resource-set-viewing-resource {
	// width is now set in vuetify.breakpoint.width watcher
	// max-width:900px;
	// margin:0 calc(50vw - 392px);
	.k-framework-list-wrapper-inner {
		max-height: calc(100vh - 120px);
	}
}

.k-resource-set-viewing-resource-embedded-showing {
	margin:0;
}

.k-resource-set-table {
	max-width: 1200px;
	margin:0 auto;
	th {
		line-height:16px;
	}
}

.k-resource-set-table-min {
	width:100px;
	white-space:nowrap;
	overflow: hidden;
}

.k-resource-set-table-min-min {
	width:40px;
	padding:0!important;
	white-space:nowrap;
}

.k-resource-set-alignment-settings {
	.v-label {
		white-space:nowrap;
		font-size:14px;
	}
}

.k-resource-factor-tooltip-outer {
	display:flex;
	flex-direction:column;
}
.k-resource-factor-tooltip {
	flex:0 0 100%;
	display:flex;
	font-size:12px;
	line-height:17px;
	.k-resource-factor-description {
		flex:1 0 auto;
		margin-right:8px;
	}
	.k-resource-factor-total {
		width:24px;
		margin-left:4px;
	}
}

.k-resource-set-last-viewed-resource {
	td {
		background-color:$v-yellow-lighten-4;
	}
}

.k-resource-alignment-validation-data {
	background-color:$v-indigo-lighten-4;
	font-size:15px;
	padding:12px;
	border-radius:10px;
	margin:16px auto;
	max-width:1000px;
}

.k-resource-alignment-validation-table {
	border-collapse: collapse;
	td, th {
		border:1px solid $v-indigo-darken-3;
		text-align:center;
		width:100px;
		background-color:#fff;
	}
	td {
		padding:8px;
		font-size:16px;
	}
	th {
		color:#000;
		font-weight:normal;
		padding:4px 4px;
		font-size:14px;
		line-height:17px;
	}
}

.k-resource-alignment-validation-active-col {
	font-weight:bold!important;
	color:$v-indigo-darken-3!important;
	.fa-chart-line { color:$v-indigo-darken-3!important; }
}

.k-resource-alignment-validation-chart-outer {
	background-color:#eee;
	color:black;
	border-radius:8px;
	margin-top:12px;
	border-radius:8px;
	padding:8px;
}
</style>
